import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Send, Beaker, Atom, Dna, Globe, BookOpen, Lightbulb, FileText, BarChart3, TestTube, FlaskConical, Download, X, AlertCircle, Wifi, WifiOff, Lock, LogOut, Clock, User, Key, Menu, ChevronLeft } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import * as XLSX from 'xlsx';
import { getRelevantISEFProjects } from './utils/isefSearch';

// Mode detection from URL parameter
const getAIMode = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('mode') || 'classic';
  }
  return 'classic';
};

// Firebase project configuration for English version
const firebaseConfig = {
  projectId: "little-rnd-experiment", // English version project ID
  // Other settings not needed when using Functions only
};

// Firebase initialization
const app = initializeApp(firebaseConfig);
const functions = getFunctions(app, 'us-central1'); // English version region

export default function ScienceResearchAI() {
  // ===== Authentication related states =====
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [authError, setAuthError] = useState('');
  const [sessionInfo, setSessionInfo] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0); // remaining time (seconds)
  const [userStatus, setUserStatus] = useState('guest'); // 'guest', 'trial', 'premium', 'unlimited'
  const [isTimerActive, setIsTimerActive] = useState(false);
  
  // License detailed information
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [lastLicenseCheck, setLastLicenseCheck] = useState(0);

  // ===== Mobile/Responsive states =====
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDocumentFullscreen, setIsDocumentFullscreen] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [showMobileWarning, setShowMobileWarning] = useState(false);

  // ===== Existing states (maintained as is) =====
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! Got any science experiments or research ideas? Feel free to share! 🧪\n\nWhat field interests you? Chemistry, biology, physics, environmental science... anything works!',
      timestamp: new Date().toLocaleTimeString(),
      isComplete: true
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [canStop, setCanStop] = useState(false);
  const [currentResearchTopic, setCurrentResearchTopic] = useState(null);
  const [researchStage, setResearchStage] = useState('exploration');
  
  // Smart trigger states
  const [conversationDepth, setConversationDepth] = useState(0); // eslint-disable-line no-unused-vars
  const [planningButtonEnabled, setPlanningButtonEnabled] = useState(false);
  const [reportButtonEnabled, setReportButtonEnabled] = useState(false);
  
  // Typing animation states
  const [typingMessageIndex, setTypingMessageIndex] = useState(null);
  const [displayedText, setDisplayedText] = useState('');
  
  // Document generation states
  const [documentMode, setDocumentMode] = useState(null);
  const [isDocumentGenerating, setIsDocumentGenerating] = useState(false);
  const [documentContent, setDocumentContent] = useState('');
  const [documentProgress, setDocumentProgress] = useState(0);
  const [documentProgressMessage, setDocumentProgressMessage] = useState('');
  const [isPrintMode, setIsPrintMode] = useState(false); // eslint-disable-line no-unused-vars
  
  // Token usage tracking
  const [tokenUsage, setTokenUsage] = useState({
    chatMessages: 0,
    systemPrompts: 0,
    generatedDocuments: 0,
    contextOverhead: 0,
    apiCallCount: 0
  });
  
  // Real Excel data state (ISEF only for English version)
  const [realExcelData, setRealExcelData] = useState([]);
  const [isExcelLoaded, setIsExcelLoaded] = useState(false);
  const [excelLoadError, setExcelLoadError] = useState('');
  
  // Connection status
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [retryCount, setRetryCount] = useState(0);

  // API Usage tracking
  const [apiUsage, setApiUsage] = useState({
    chatCount: 0,
    docCount: 0,
    chatLimit: 500,
    docLimit: 25,
    productName: 'Standard'
  });

  const messagesEndRef = useRef(null);
  const maxTokens = 20000;

  // AI Mode (classic or natural) based on URL parameter
  const aiMode = useMemo(() => getAIMode(), []);

  // ===== License management functions =====
  
  // License information processing
  const processLicenseInfo = useCallback((sessionData) => {
    const currentTime = new Date();
    
    let licenseType = 'unknown';
    let expireTime = null;
    let timeLeftSeconds = 0;
    let isExpired = false;
    
    if (sessionData.code === '0000') {
      // Free trial
      licenseType = 'trial';
      expireTime = new Date(currentTime.getTime() + (sessionData.timeLeft * 1000));
      timeLeftSeconds = sessionData.timeLeft;
      isExpired = timeLeftSeconds <= 0;
    } else if (sessionData.type === 'unlimited') {
      // Unlimited license
      licenseType = 'unlimited';
      expireTime = null;
      timeLeftSeconds = Infinity;
      isExpired = false;
    } else if (sessionData.remainingTime !== undefined) {
      // Backend provides remainingTime directly (priority use)
      licenseType = sessionData.duration_days > 0 ? 'premium_days' : 'premium_minutes';
      timeLeftSeconds = sessionData.remainingTime;
      if (sessionData.expireDate) {
        expireTime = new Date(sessionData.expireDate);
      }
      isExpired = timeLeftSeconds <= 0;
    } else if (sessionData.duration_days) {
      // Day-based license (fallback)
      licenseType = 'premium_days';
      const startTime = new Date(sessionData.startTime || currentTime);
      expireTime = new Date(startTime.getTime() + (sessionData.duration_days * 24 * 60 * 60 * 1000));
      timeLeftSeconds = Math.max(0, Math.floor((expireTime - currentTime) / 1000));
      isExpired = timeLeftSeconds <= 0;
    } else if (sessionData.duration_minutes) {
      // Minute-based license (fallback)
      licenseType = 'premium_minutes';
      const startTime = new Date(sessionData.startTime || currentTime);
      expireTime = new Date(startTime.getTime() + (sessionData.duration_minutes * 60 * 1000));
      timeLeftSeconds = Math.max(0, Math.floor((expireTime - currentTime) / 1000));
      isExpired = timeLeftSeconds <= 0;
    }
    
    const licenseInfo = {
      type: licenseType,
      code: sessionData.code,
      startTime: sessionData.startTime ? new Date(sessionData.startTime) : currentTime,
      expireTime: expireTime,
      timeLeft: timeLeftSeconds,
      isExpired: isExpired,
      status: isExpired ? 'expired' : 'active',
      displayName: getLicenseDisplayName(licenseType, sessionData),
      statusIcon: getLicenseStatusIcon(licenseType, isExpired, timeLeftSeconds)
    };
    
    setLicenseInfo(licenseInfo);
    setTimeLeft(timeLeftSeconds);
    
    // Update user status
    if (isExpired) {
      setUserStatus('expired');
      setIsTimerActive(false);
    } else if (licenseType === 'trial') {
      setUserStatus('trial');
      setIsTimerActive(true);
    } else if (licenseType === 'unlimited') {
      setUserStatus('unlimited');
      setIsTimerActive(false);
    } else {
      setUserStatus('premium');
      setIsTimerActive(timeLeftSeconds < Infinity);
    }
    
    return licenseInfo;
  }, []);
  
  // License display name function
  const getLicenseDisplayName = useCallback((licenseType, sessionData = null) => {
    // Use productName from backend if available
    if (sessionData?.productName) {
      return sessionData.productName;
    }
    
    switch (licenseType) {
      case 'trial': return 'Free Trial';
      case 'unlimited': return 'Unlimited License';
      case 'premium_days': return 'Premium (Days)';
      case 'premium_minutes': return 'Premium (Minutes)';
      default: return 'Unknown';
    }
  }, []);
  
  // License status icon
  const getLicenseStatusIcon = useCallback((licenseType, isExpired, timeLeftSeconds) => {
    if (isExpired) return '❌';
    if (licenseType === 'unlimited') return '♾️';
    if (licenseType === 'trial' && timeLeftSeconds <= 60) return '⚠️';
    if (timeLeftSeconds <= 3600) return '⏰'; // 1 hour or less
    return '✅';
  }, []);
  
  // Time formatting function
  const formatTimeLeft = useCallback((seconds) => {
    if (seconds === Infinity) return 'Unlimited';
    if (seconds <= 0) return 'Expired';
    
    const days = Math.floor(seconds / (24 * 60 * 60));
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((seconds % (60 * 60)) / 60);
    const secs = seconds % 60;
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  }, []);
  
  // Session data save function
  const saveSessionData = useCallback((responseData) => {
    try {
      // Maintain existing method (compatibility)
      localStorage.setItem('accessSession', JSON.stringify(responseData));
      
      // Save new sessionData separately (backend requirement)
      if (responseData.sessionData) {
        localStorage.setItem('sessionData', JSON.stringify(responseData.sessionData));
        console.log('✅ sessionData saved separately:', responseData.sessionData);
      }
    } catch (error) {
      console.error('❌ sessionData save failed:', error);
    }
  }, []);

  // Session data retrieval function
  const getStoredSessionData = useCallback(() => {
    try {
      // Priority 1: New sessionData
      const sessionDataStr = localStorage.getItem('sessionData');
      if (sessionDataStr) {
        const sessionData = JSON.parse(sessionDataStr);
        console.log('📄 Using stored sessionData:', sessionData);
        return sessionData;
      }
      
      // Priority 2: Extract sessionData from existing accessSession
      const accessSessionStr = localStorage.getItem('accessSession');
      if (accessSessionStr) {
        const accessSession = JSON.parse(accessSessionStr);
        if (accessSession.sessionData) {
          console.log('📄 Extracting sessionData from accessSession:', accessSession.sessionData);
          return accessSession.sessionData;
        }
        // Priority 3: Use accessSession itself as sessionData (existing compatibility)
        console.log('📄 Using accessSession as sessionData:', accessSession);
        return accessSession;
      }
      
      console.log('⚠️ No stored sessionData found');
      return null;
    } catch (error) {
      console.error('❌ sessionData parsing error:', error);
      return null;
    }
  }, []);
  
  // License validity check
  const checkLicenseValidity = useCallback(async (code, skipCache = false) => {
    const now = Date.now();
    
    // Cache check (server check only every 30 seconds)
    if (!skipCache && sessionInfo && (now - lastLicenseCheck) < 30000) {
      console.log('🎯 Using cached license info');
      return { isValid: true, licenseInfo, message: 'Using cached info' };
    }
    
    console.log('🔒 Checking license on server:', code);

    try {
      // IP License - 등록된 학교 IP에서만 자동입장, 학교 밖에선 코드 입력 필요
      if (code === 'IP_LICENSE') {
        console.log('🏫 IP License - 학교 IP 외부 접속 감지, 세션 초기화');
        localStorage.removeItem('accessSession');
        localStorage.removeItem('sessionData');
        return {
          isValid: false,
          licenseInfo: null,
          message: '학교 네트워크에서만 자동 입장됩니다. 코드를 입력해주세요.'
        };
      }

      // Free trial code also sent to backend now
      if (code === '0000') {
        // localStorage-based primary check
        const trialRecord = localStorage.getItem('trialCompleted');
        if (trialRecord) {
          try {
            const parsed = JSON.parse(trialRecord);
            if (parsed.completed) {
              return {
                isValid: false,
                licenseInfo: null,
                message: 'Free trial already completed on this device. Please try on another device.'
              };
            }
          } catch (error) {
            console.error('trialRecord parsing error:', error);
          }
        }
        
        console.log('🎯 Free trial code - backend IP check');
        
        const verifyFreeTrial = httpsCallable(functions, 'verifyFreeTrial');
        const result = await verifyFreeTrial({ code: '0000' });
        
        const data = result.data;
        console.log('📦 Free trial server response:', data);
        
        if (data.success) {
          const processedInfo = processLicenseInfo(data.sessionData);
          setLastLicenseCheck(now);
          
          return {
            isValid: true,
            licenseInfo: processedInfo,
            message: data.message
          };
        } else {
          return {
            isValid: false,
            licenseInfo: null,
            message: data.message
          };
        }
      }
      
      // Only paid codes sent to backend - Firebase Functions response handling modified
      const verifyCode = httpsCallable(functions, 'verifyAccessCode');
      const result = await verifyCode({ code: code });
      
      // Important: Firebase Functions puts response in result.data!
      const data = result.data;
      console.log('📦 Server response data:', data);
      
      // Save server response to localStorage
      saveSessionData(data);
      
      setLastLicenseCheck(now);
      
      if (data.success) {
        // Modify to match server response data structure
        const sessionData = {
          code: code,
          type: data.accessType === 'premium' ? 'premium' : data.accessType,
          duration_days: data.validDays || 0,
          duration_minutes: data.validMinutes || 0,
          duration: data.duration || '',
          productName: data.productName || data.ticketType || '',
          startTime: data.startDate || new Date().toISOString(),
          expireDate: data.expireDate || null,
          accessType: data.accessType || 'unknown',
          remainingTime: data.remainingTime || 0
        };
        
        console.log('✅ Session data to process:', sessionData);
        
        const processedInfo = processLicenseInfo(sessionData);
        
        console.log('📄 Processed license info:', processedInfo);
        console.log('📄 License displayName:', processedInfo?.displayName);
        console.log('📄 License type:', processedInfo?.type);
        
        if (processedInfo.isExpired) {
          return {
            isValid: false,
            licenseInfo: processedInfo,
            message: `License has expired. (Expiry: ${processedInfo.expireTime?.toLocaleString()})`
          };
        }
        
        return {
          isValid: true,
          licenseInfo: processedInfo,
          message: `${processedInfo.displayName} authentication complete!`
        };
      } else {
        return {
          isValid: false,
          licenseInfo: null,
          message: data.message || 'Invalid access code.'
        };
      }
    } catch (error) {
      console.error('❌ License check error:', error);
      return {
        isValid: false,
        licenseInfo: null,
        message: 'Network error occurred. Please try again.'
      };
    }
  }, [sessionInfo, lastLicenseCheck, licenseInfo, processLicenseInfo, saveSessionData]);
  
  // Access code verification (improved version)
  const verifyAccessCode = useCallback(async (code) => {
    setIsVerifying(true);
    setAuthError('');
    
    // Free trial code pre-check
    if (code === '0000') {
      const trialRecord = localStorage.getItem('trialCompleted');
      if (trialRecord) {
        try {
          const parsed = JSON.parse(trialRecord);
          if (parsed.completed) {
            setAuthError('Free trial already completed on this device.');
            setIsVerifying(false);
            return;
          }
        } catch (error) {
          console.error('trialRecord check error:', error);
        }
      }
    }
    
    try {
      console.log('🔒 Verifying access code:', code);
      
      const result = await checkLicenseValidity(code, true); // Force server check
      
      if (result.isValid) {
        setIsAuthenticated(true);
        setSessionInfo({
          code: code,
          ...result.licenseInfo
        });

        // Fetch API usage status
        try {
          const getUsageStatus = httpsCallable(functions, 'getUsageStatus');
          const validDays = result.licenseInfo?.validDays || (code === '0000' ? 0 : 30);
          const usageResult = await getUsageStatus({ accessCode: code, validDays });

          if (usageResult.data?.success) {
            setApiUsage(usageResult.data.usage);
            console.log('📊 API Usage loaded:', usageResult.data.usage);
          }
        } catch (usageError) {
          console.error('Usage fetch error:', usageError);
        }

        // Generate welcome message
        const welcomeMessage = {
          role: 'assistant',
          content: generateWelcomeMessage(result.licenseInfo),
          timestamp: new Date().toLocaleTimeString(),
          isComplete: true
        };
        setMessages(prev => [...prev, welcomeMessage]);

        console.log('✅ Authentication successful!', result.licenseInfo);
      } else {
        setAuthError(result.message);
      }
    } catch (error) {
      console.error('❌ Code verification error:', error);
      setAuthError('Network error occurred. Please try again.');
    }
    
    setIsVerifying(false);
  }, [checkLicenseValidity]);
  
  // License type-specific welcome message generation
  const generateWelcomeMessage = useCallback((licenseInfo) => {
    const icon = licenseInfo.statusIcon;
    const displayName = licenseInfo.displayName;
    
    switch (licenseInfo.type) {
      case 'trial':
        return `🎉 **${displayName} Started!** ${icon}\n\nExplore all features freely for 15 minutes!\n\n• 🔬 Generate research proposals\n• 📝 Create experiment reports\n• 🏆 Search competition projects with AI\n• 💬 Science consultation & experiment design\n\nYou can check remaining time in the top right! ⏰`;
      
      case 'unlimited':
        return `♾️ **${displayName} Authentication Complete!**\n\nYou can use all features unlimited forever!\n\n• ⭐ Unlimited conversations & document generation\n• 🔬 Advanced research proposal templates\n• 📊 Detailed experiment report creation\n• 🎯 Customized science experiment design\n• 🏆 Unlimited competition project search\n\nExplore all possibilities of scientific research with your permanent license! 🚀`;
      
      case 'premium_days':
      case 'premium_minutes':
        const timeInfo = licenseInfo.timeLeft === Infinity ? 'Unlimited' : formatTimeLeft(licenseInfo.timeLeft);
        return `✨ **${displayName} Authentication Complete!** ${icon}\n\nRemaining time: **${timeInfo}**\n\nYou can use all premium features!\n\n• ⭐ Extended conversations & document generation\n• 🔬 Advanced research proposal templates\n• 📊 Detailed experiment report creation\n• 🎯 Customized science experiment design\n• 🏆 Competition project search\n\nExplore all possibilities of scientific research as a premium user! 🚀`;
      
      default:
        return `✅ **Authentication Complete!**\n\nYou can use all features!\n\n• 🔬 Generate research proposals\n• 📝 Create experiment reports\n• 🏆 Search competition projects\n• 💬 Science consultation`;
    }
  }, [formatTimeLeft]);

  // Session status check function with checkSessionStatus call
  const checkSession = useCallback(async () => {
    if (!isAuthenticated || !sessionInfo) return;

    console.log('📄 Checking session status...');

    try {
      // Free trial (0000) doesn't do backend check
      if (sessionInfo.code === '0000') {
        console.log('🎯 Free trial - skip backend session check');
        return;
      }

      // IP License - check locally only (no backend call needed)
      if (sessionInfo.code === 'IP_LICENSE') {
        console.log('🏫 IP License - local expiry check only');
        const storedData = getStoredSessionData();
        if (storedData?.expireDate) {
          const expireDate = new Date(storedData.expireDate);
          const now = new Date();
          if (now > expireDate) {
            console.log('🚨 IP License expired');
            handleSessionExpired();
            return;
          }
          const remainingTime = Math.floor((expireDate - now) / 1000);
          setTimeLeft(remainingTime);
          console.log('✅ IP License valid, remaining:', remainingTime);
        }
        return;
      }
      
      // Add: checkSessionStatus call
      const storedSessionData = getStoredSessionData();
      if (storedSessionData) {
        console.log('📡 Calling checkSessionStatus...', storedSessionData);
        
        const checkSessionStatus = httpsCallable(functions, 'checkSessionStatus');
        const result = await checkSessionStatus({ sessionData: storedSessionData });
        
        const data = result.data;
        console.log('📊 checkSessionStatus response:', data);
        
        if (!data.success) {
          console.log('🚨 Session expiry detected');
          handleSessionExpired();
          return;
        }
        
        // Update remaining time if session is valid
        if (data.remainingTime !== undefined) {
          setTimeLeft(data.remainingTime);
          console.log('✅ Session status updated, remaining time:', data.remainingTime);
        }
      } else {
        console.log('⚠️ No stored sessionData - check with existing method');
        const result = await checkLicenseValidity(sessionInfo.code);
        
        if (!result.isValid) {
          console.log('🚨 Session expiry detected');
          if (result.licenseInfo?.isExpired) {
            handleTrialExpired();
          } else {
            handleSessionExpired();
          }
        } else {
          // Update license info
          if (result.licenseInfo) {
            const updatedInfo = processLicenseInfo(result.licenseInfo);
            console.log('✅ Session info updated');
          }
        }
      }
    } catch (error) {
      console.error('Session check error:', error);
      // Ignore network errors and continue using local timer
    }
  }, [isAuthenticated, sessionInfo, getStoredSessionData, checkLicenseValidity, processLicenseInfo]);

  // Free trial usage update function (newly added)
  const updateTrialUsage = useCallback(async (usedSeconds) => {
    if (sessionInfo?.code === '0000' && usedSeconds > 0) {
      try {
        const updateUsage = httpsCallable(functions, 'updateFreeTRialUsage');
        await updateUsage({
          usedSeconds,
          completed: usedSeconds >= 900, // Completion status
          finalUpdate: usedSeconds >= 900 // Final update flag
        });
        console.log(`📊 Free trial usage updated: ${usedSeconds}s`);
      } catch (error) {
        console.error('Usage update failed:', error);
      }
    }
  }, [sessionInfo]);

  // Reset all data function - added from Korean version
  const resetAllData = useCallback(() => {
    console.log('🔄 Resetting all conversation data');
    
    // Reset conversation
    setMessages([
      {
        role: 'assistant',
        content: 'Hello! Got any science experiments or research ideas? Feel free to share! 🧪\n\nWhat field interests you? Chemistry, biology, physics, environmental science... anything works!',
        timestamp: new Date().toLocaleTimeString(),
        isComplete: true
      }
    ]);
    
    // Reset research states
    setCurrentResearchTopic(null);
    setResearchStage('exploration');
    setPlanningButtonEnabled(false);
    setReportButtonEnabled(false);
    
    // Reset document states
    setDocumentMode(null);
    setDocumentContent('');
    setIsDocumentGenerating(false);
    setDocumentProgress(0);
    setDocumentProgressMessage('');
    
    // Reset token usage (including apiCallCount)
    setTokenUsage({
      chatMessages: 0,
      systemPrompts: 0,
      generatedDocuments: 0,
      contextOverhead: 0,
      apiCallCount: 0
    });
    
    // Reset input
    setInputValue('');
    
    console.log('✅ All data reset complete');
  }, []);

  // Logout function (improved version)
  const handleLogout = useCallback(() => {
    console.log('🚪 Starting logout process');
    
    // Update usage time if using free trial
    if (sessionInfo?.code === '0000' && timeLeft < 900) {
      const usedTime = 900 - timeLeft;
      updateTrialUsage(Math.max(usedTime, 0));
    }
    
    setIsAuthenticated(false);
    setAccessCode('');
    setSessionInfo(null);
    setLicenseInfo(null);
    setTimeLeft(0);
    setUserStatus('guest');
    setIsTimerActive(false);
    setAuthError('');
    setLastLicenseCheck(0);
    
    // Clean up localStorage
    localStorage.removeItem('accessSession');
    localStorage.removeItem('sessionData');
    
    console.log('📄 State reset complete');
    
    // Reset conversation
    setMessages([
      {
        role: 'assistant',
        content: 'Hello! Got any science experiments or research ideas? Feel free to share! 🧪\n\nWhat field interests you? Chemistry, biology, physics, environmental science... anything works!',
        timestamp: new Date().toLocaleTimeString(),
        isComplete: true
      }
    ]);
    
    // Reset other states
    setCurrentResearchTopic(null);
    setPlanningButtonEnabled(false);
    setReportButtonEnabled(false);
    setDocumentMode(null);
    setDocumentContent('');
    
    console.log('✅ Logout complete - redirecting to login screen');
  }, [sessionInfo, timeLeft, updateTrialUsage]);

  // Free trial expiry handling (improved version)
  const handleTrialExpired = useCallback(() => {
    console.log('⏰ Free trial expiry handling started');
    
    // Record free trial completion (localStorage)
    const trialRecord = {
      completed: true,
      completedAt: new Date().toISOString(),
      deviceFingerprint: navigator.userAgent + window.screen.width + window.screen.height
    };
    localStorage.setItem('trialCompleted', JSON.stringify(trialRecord));
    
    // Calculate actual usage time and update server
    const originalTime = 900;
    const usedTime = originalTime - timeLeft;
    updateTrialUsage(Math.max(usedTime, 0));

    setIsTimerActive(false);
    setTimeLeft(0);
    setUserStatus('expired');

    if (licenseInfo) {
      setLicenseInfo(prev => prev ? { ...prev, isExpired: true, status: 'expired' } : null);
    }

    const expiredMessage = {
      role: 'assistant',
      content: `⏰ **Free trial has expired!**\n\nHow was your 15-minute experience? 😊\n\n**To continue using:**\n• Purchase a paid access code or\n• Try the free trial again on another device\n\nThank you! 🙏`,
      timestamp: new Date().toLocaleTimeString(),
      isComplete: true
    };
    setMessages(prev => [...prev, expiredMessage]);
    
    console.log('📝 Expiry message added');
    
    // Auto logout after 3 seconds
    setTimeout(() => {
      console.log('🚪 Auto logout execution');
      handleLogout();
    }, 3000);
  }, [timeLeft, updateTrialUsage, licenseInfo, handleLogout]);

  // Session expiry handling (improved version)
  const handleSessionExpired = useCallback(() => {
    console.log('🔒 Session expiry handling started');
    
    const expiredMessage = {
      role: 'assistant',
      content: `🔒 **Session has expired.**\n\nPlease enter your access code again.`,
      timestamp: new Date().toLocaleTimeString(),
      isComplete: true
    };
    setMessages(prev => [...prev, expiredMessage]);
    
    setTimeout(() => {
      handleLogout();
    }, 2000);
  }, [handleLogout]);

  // ===== Existing functions (fully restored) =====
  
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Token count estimation function
  const estimateTokens = useCallback((text) => {
    return Math.ceil(text.length / 3.5);
  }, []);

  const calculateUsage = useCallback(() => {
    const chatTokens = messages.reduce((sum, msg) =>
      sum + estimateTokens(msg.content), 0
    );
    const systemTokens = estimateTokens("System prompt") * tokenUsage.apiCallCount;
    const contextOverhead = chatTokens * tokenUsage.apiCallCount * 0.5;

    const totalTokens = chatTokens + systemTokens + contextOverhead;
    return Math.min((totalTokens / maxTokens) * 100, 100);
  }, [messages, estimateTokens, tokenUsage.apiCallCount]);

  const getCurrentTokens = useCallback(() => {
    const chatTokens = messages.reduce((sum, msg) =>
      sum + estimateTokens(msg.content), 0
    );
    const systemTokens = estimateTokens("System prompt") * tokenUsage.apiCallCount;
    const contextOverhead = chatTokens * tokenUsage.apiCallCount * 0.5;

    return chatTokens + systemTokens + contextOverhead;
  }, [messages, estimateTokens, tokenUsage.apiCallCount]);

  const getUsageColor = useCallback(() => {
    const usage = calculateUsage();
    if (usage < 50) return 'bg-green-500';
    if (usage < 80) return 'bg-yellow-500';
    return 'bg-red-500';
  }, [calculateUsage]);

  // Connection status check function
  const checkConnectionStatus = useCallback(async () => {
    try {
      const checkSessionStatus = httpsCallable(functions, 'checkSessionStatus');
      const result = await checkSessionStatus({ test: true });
      
      if (result.data) {
        setConnectionStatus('connected');
        setRetryCount(0);
        return true;
      } else {
        setConnectionStatus('unstable');
        return false;
      }
    } catch (error) {
      console.warn("Connection status check failed:", error);
      setConnectionStatus('disconnected');
      return false;
    }
  }, []);

  // Name masking function (privacy protection)
  const maskAuthorName = useCallback((authorName) => {
    if (!authorName || authorName === 'No Author' || authorName.trim() === '') {
      return 'No Author Information';
    }

    // Multiple authors (separated by comma or '&')
    const authors = authorName.split(/[,&]/).map(name => name.trim());
    
    const maskedAuthors = authors.map(name => {
      if (!name) return '';
      
      // English name handling (first letter only, rest masked)
      const englishNameMatch = name.match(/^([A-Za-z])([A-Za-z]+)(.*)$/);
      if (englishNameMatch) {
        const firstLetter = englishNameMatch[1];
        const restLength = englishNameMatch[2].length;
        const suffix = englishNameMatch[3]; // surname or additional info
        return firstLetter + '*'.repeat(restLength) + (suffix ? ' ' + suffix.charAt(0) + '*'.repeat(Math.max(0, suffix.length-1)) : '');
      }
      
      // Other formats (show first letter only)
      if (name.length > 1) {
        return name.charAt(0) + '*'.repeat(name.length - 1);
      }
      
      return name;
    });
    
    return maskedAuthors.filter(name => name).join(', ');
  }, []);

  // Real Excel data loading function (ISEF only for English version)
  const loadRealExcelData = useCallback(async () => {
    const maxRetries = 3;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`📊 Loading Excel data attempt ${attempt}/${maxRetries}...`);

        // Load ISEF data
        console.log("📥 Downloading ISEF file...");
        const isefResponse = await fetch('/data/ISEF International Projects.xlsx', {
          method: 'GET',
          cache: 'force-cache', // Use cache
          headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          }
        });

        if (!isefResponse.ok) {
          throw new Error(`ISEF file loading failed: ${isefResponse.status} - ${isefResponse.statusText}`);
        }

        const isefArrayBuffer = await isefResponse.arrayBuffer();
        console.log(`✅ ISEF file size: ${(isefArrayBuffer.byteLength / 1024 / 1024).toFixed(2)}MB`);

        const isefWorkbook = XLSX.read(isefArrayBuffer, {
          cellStyles: true,
          cellFormulas: true,
          cellDates: true
        });
        const isefSheet = isefWorkbook.Sheets[isefWorkbook.SheetNames[0]];
        const isefData = XLSX.utils.sheet_to_json(isefSheet);

        // Data validation
        if (isefData.length === 0) {
          throw new Error('No data found in Excel file');
        }

        setRealExcelData(isefData);

        setIsExcelLoaded(true);
        setExcelLoadError('');
        console.log(`✅ Excel data loading complete! ISEF: ${isefData.length.toLocaleString()} projects`);
        
        // Exit loop on success
        return;
        
      } catch (error) {
        console.error(`❌ Excel data loading attempt ${attempt} failed:`, error);
        
        if (attempt === maxRetries) {
          // More detailed error message on final failure
          const detailedError = `Excel loading final failure (${maxRetries} attempts): ${error.message}`;
          setExcelLoadError(detailedError);
          
          // Fallback: set empty data so app still works
          setRealExcelData([]);
          setIsExcelLoaded(true); // Mark as loaded even with error
          
          // Notify user
          const errorMessage = {
            role: 'assistant',
            content: `⚠️ **Excel Data Loading Failed**\n\n${detailedError}\n\nCompetition project search is temporarily unavailable, but all other features work normally!\n\n• Research proposal/experiment report generation ✅\n• Science experiment consultation ✅\n• AI analysis & advice ✅`,
            timestamp: new Date().toLocaleTimeString(),
            isComplete: true
          };
          
          setMessages(prev => [...prev, errorMessage]);
        } else {
          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, attempt * 1000));
        }
      }
    }
  }, []);

  // Search real Excel data (ISEF only for English version)
  const searchRealExcelData = useCallback(async (keyword) => {
    if (!keyword.trim() || !isExcelLoaded) {
      return { results: [], totalResults: 0 };
    }

    console.log(`🔍 Project relevance search: "${keyword}"`);
    
    // Create comprehensive search terms
    const searchTerms = [
      keyword,                              
      keyword.toLowerCase(),
      ...keyword.split(' ').filter(word => word.length > 1),
      // Science field-specific related terms
      ...(keyword.includes('solar') ? ['photovoltaic', 'renewable', 'energy'] : []),
      ...(keyword.includes('environment') ? ['ecology', 'pollution', 'sustainability'] : []),
      ...(keyword.includes('robot') ? ['automation', 'artificial', 'machine'] : []),
      ...(keyword.includes('nano') ? ['microscale', 'nanomaterial', 'nanotechnology'] : [])
    ].filter((term, index, arr) => term && term.length > 0 && arr.indexOf(term) === index);
    
    console.log(`🌐 Expanded search terms (${searchTerms.length}):`, searchTerms);

    // Enhanced relevance score calculation function
    const calculateRelevanceScore = (item, searchTerms) => {
      let score = 0;
      
      const fieldsToSearch = [
        { field: item['Project Title'] || item.title || '', weight: 20 },
        { field: item['Category'] || item.category || '', weight: 8 },    
        { field: item['Finalist Name(s)'] || item.author || '', weight: 1 },
        { field: item['Awards Won'] || item.awards || '', weight: 3 }
      ];
      
      fieldsToSearch.forEach(({ field, weight }) => {
        if (!field) return;
        
        const normalizedField = field.toString().toLowerCase();
        
        searchTerms.forEach(term => {
          if (!term) return;
          const normalizedTerm = term.toLowerCase();
          
          // Exact word boundary match (highest score)
          const wordBoundaryRegex = new RegExp(`\\b${normalizedTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
          if (wordBoundaryRegex.test(normalizedField)) {
            score += weight * 5;
          }
          // Complete match
          else if (normalizedField === normalizedTerm) {
            score += weight * 4;
          }
          // Word-unit split exact inclusion
          else if (normalizedField.split(/[\s\-_.,]+/).includes(normalizedTerm)) {
            score += weight * 3;
          }
          // Partial string inclusion (length 3+)
          else if (normalizedTerm.length >= 3 && normalizedField.includes(normalizedTerm)) {
            score += weight * 1.5;
          }
          // Prefix match (4+ chars)
          else if (normalizedTerm.length > 4 && normalizedField.includes(normalizedTerm.slice(0, -1))) {
            score += weight * 0.5;
          }
        });
      });
      
      // Additional: Science field-specific bonus points
      const category = (item['Category'] || '').toLowerCase();
      const title = (item['Project Title'] || '').toLowerCase();
      const keywordLower = keyword.toLowerCase();
      
      // Field matching bonus
      if (keywordLower.includes('environment') && (category.includes('environmental') || category.includes('earth'))) score += 15;
      if (keywordLower.includes('biology') && (category.includes('biology') || category.includes('biomedical'))) score += 15;
      if (keywordLower.includes('chemistry') && category.includes('chemistry')) score += 15;
      if (keywordLower.includes('physics') && category.includes('physics')) score += 15;
      if (keywordLower.includes('engineering') && category.includes('engineering')) score += 15;
      if (keywordLower.includes('computer') && (category.includes('computer') || category.includes('computational'))) score += 15;
      
      // Title core keyword match additional bonus
      if (title.includes(keyword.toLowerCase())) score += 10;
      
      return Math.round(score); // Remove decimals
    };

    // ISEF data search
    const resultsWithScore = realExcelData
      .map((item, index) => ({
        ...item,
        originalIndex: index,
        relevanceScore: calculateRelevanceScore(item, searchTerms)
      }))
      .filter(item => item.relevanceScore >= 15)
      .sort((a, b) => b.relevanceScore - a.relevanceScore);

    // Determine number of results to display (max 5)
    const resultsToShow = resultsWithScore.slice(0, 5);

    // ISEF title processing + privacy protection
    const finalResults = resultsToShow.map((item) => {
      const originalTitle = item['Project Title'] || item.title || 'No Title';
      const originalAuthor = item['Finalist Name(s)'] || item.author || '';

      return {
        title: originalTitle,
        category: item['Category'] || item.category || 'No Category',
        year: item['Year'] || item.year || '',
        author: maskAuthorName(originalAuthor),
        country: item['Fair Country'] || item.country || '',
        state: item['Fair State'] || item.state || '',
        awards: item['Awards Won'] || item.awards || '',
        relevanceScore: item.relevanceScore,
        originalIndex: item.originalIndex
      };
    });

    return {
      results: finalResults,
      totalResults: finalResults.length,
      searchStats: {
        total: resultsWithScore.length,
        showing: finalResults.length,
        truncated: resultsWithScore.length > 5
      },
      searchTermsUsed: searchTerms
    };
  }, [realExcelData, isExcelLoaded, maskAuthorName]);

  // Start new message typing
  const startTypingMessage = useCallback((messageContent) => {
    const newMessage = {
      role: 'assistant',
      content: messageContent,
      timestamp: new Date().toLocaleTimeString(),
      isComplete: false
    };
    
    setMessages(prev => {
      const newMessages = [...prev, newMessage];
      const newIndex = newMessages.length - 1;
      
      setTimeout(() => {
        setTypingMessageIndex(newIndex);
        setDisplayedText('');
      }, 100);
      
      return newMessages;
    });
  }, []);

  // Scientific topic analysis via Firebase Functions - retry logic added
  const analyzeScientificTopic = useCallback(async (text, fullConversationHistory = messages) => {
    const maxRetries = 2;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const conversationContext = fullConversationHistory
          .map(msg => `${msg.role}: ${msg.content}`)
          .join('\n');
        
        const prompt = `Analyze the latest user message considering the full conversation context:

Full conversation history:
${conversationContext}

Latest user message: "${text}"

Considering the full context, judge scientific validity and respond in JSON:

{
  "isScientific": true/false,
  "topic": "Specific science topic name (English)",
  "field": "physics/chemistry/biology/environmental/electronics/medicine/earth_science/astronomy/other",
  "experimentable": true/false,
  "safetyLevel": "safe/caution/dangerous",
  "educationalValue": "high/medium/low",
  "reason": "Judgment basis (one line)"
}

Judgment criteria:
- isScientific: Related to scientific principles or phenomena, or has scientific context from previous conversation?
- experimentable: Feasible for high school level experiment/research?
- safetyLevel: safe (safe), caution (needs caution), dangerous (dangerous)
- educationalValue: Degree of educational value

Safe science topics (always judge as safe):
- Nanotubes, graphene, nanomaterials, semiconductors, solar cells
- Plant growth, microplastics, environmental science, biology experiments
- Physics experiments, chemistry experiments (general level)
- CVD, SEM, AFM and other general analytical techniques

DO NOT OUTPUT ANYTHING OTHER THAN VALID JSON.`;

        const callClaude = httpsCallable(functions, 'callClaude');
        const response = await callClaude({
           messages: [{ role: "user", content: prompt }],
           max_tokens: 300
           });
        
        const data = response.data;
        let responseText = data.content[0].text;
        
        responseText = responseText.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
        
        const analysis = JSON.parse(responseText);
        
        setTokenUsage(prev => ({
          ...prev,
          apiCallCount: prev.apiCallCount + 1
        }));
        
        return analysis; // Return immediately on success
        
      } catch (error) {
        console.error(`Topic analysis attempt ${attempt} failed:`, error);
        
        if (attempt === maxRetries) {
          // Final fallback analysis
          const safeKeywords = ['nanotube', 'experiment', 'research', 'design', 'analysis', 'CVD', 'SEM', 'AFM', 'synthesis', 'measurement', 'observation', 'data', 'microplastic', 'plant', 'solar cell', 'diode'];
          const hasDirectScienceKeywords = safeKeywords.some(keyword => text.includes(keyword));
          
          return {
            isScientific: hasDirectScienceKeywords,
            topic: hasDirectScienceKeywords ? "Science Research" : "General Conversation",
            field: "other",
            experimentable: hasDirectScienceKeywords,
            safetyLevel: "safe",
            educationalValue: hasDirectScienceKeywords ? "high" : "low",
            reason: "Local keyword analysis result (network error)"
          };
        }
        
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }, [messages]);
  
  // AI-based topic transition detection function
  const checkTopicTransition = useCallback(async (recentMessages) => {
    try {
      const prompt = `Analyze the following recent conversation to determine if there has been a transition from a dangerous topic to a SAFE approach:

"${recentMessages}"

Respond in JSON:
{
  "hasTransitioned": true/false,
  "newSafeTopic": "specific safe topic/approach name",
  "reason": "reasoning for the judgment"
}

Judgment criteria - return hasTransitioned: true if ANY of these apply:

**Type A: Safe Physical Experiments**
- Safe alternative experiments feasible at high school level
- Observation, measurement, recording focused experiments
- Examples: CD spectroscope, neon sign observation, baking soda+vinegar, magnet experiments, pH measurements, color change observations

**Type B: Theoretical/Simulation Approaches (ALSO SAFE)**
- Theoretical analysis or literature review
- Mathematical modeling or prediction models
- Computer simulation or code-based modeling
- Data analysis of existing datasets
- Theoretical research reports
- Keywords: "theoretical", "simulation", "mathematical model", "prediction model", "literature review", "theoretical report", "data analysis", "existing data"

IMPORTANT: Theoretical and simulation approaches are SAFE and should return hasTransitioned: true with appropriate newSafeTopic like "Theoretical Analysis: [topic]" or "Simulation Study: [topic]"

DO NOT OUTPUT ANYTHING OTHER THAN VALID JSON.`;

      const callClaude = httpsCallable(functions, 'callClaude');
      const response = await callClaude({
        messages: [{ role: "user", content: prompt }],
        max_tokens: 300
      });
      
      const data = response.data;
      let responseText = data.content[0].text;
      
      responseText = responseText.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
      
      const analysis = JSON.parse(responseText);
      
      setTokenUsage(prev => ({
        ...prev,
        apiCallCount: prev.apiCallCount + 1
      }));
      
      return analysis;
    } catch (error) {
      console.error("Topic transition analysis error:", error);
      // Fallback: basic safe keyword check
      const safeKeywords = ['CD', 'neon light', 'spectroscope', 'safe', 'alternative', 'baking soda', 'vinegar', 'magnet', 'thermometer', 'pH', 'observation', 'measurement'];
      const hasSafeKeyword = safeKeywords.some(keyword => recentMessages.includes(keyword));
      
      return {
        hasTransitioned: hasSafeKeyword,
        newSafeTopic: hasSafeKeyword ? "Safe Experiment" : null,
        reason: "Fallback keyword analysis"
      };
    }
  }, []);

  // Trigger activation logic - more careful activation
  const updateTriggerStates = useCallback(async (userMessage) => {
    const analysis = await analyzeScientificTopic(userMessage, messages);
    
    // Calculate current conversation turn count (user messages only)
    const userMessageCount = messages.filter(msg => msg.role === 'user').length + 1; // +1 for current message
    
    // Check scientific conversation continuity
    const recentMessages = messages.slice(-4); // Last 4 messages
    const scientificContinuity = recentMessages.filter(msg => {
      const safeKeywords = ['nanotube', 'experiment', 'research', 'design', 'analysis', 'CVD', 'SEM', 'AFM', 'synthesis', 'measurement', 'observation', 'data', 'microplastic', 'plant', 'solar cell', 'diode', 'chemistry', 'biology', 'physics'];
      return safeKeywords.some(keyword => msg.content.includes(keyword));
    }).length;
    
    // Check topic specificity
    const isSpecificTopic = analysis.topic && analysis.topic !== 'Science Research' && analysis.topic !== 'General Conversation';
    
    // Check clarity of experimental intent
    const hasExperimentWords = ['want to experiment', 'want to research', 'design', 'experiment method', 'how to do', 'want to make', 'want to measure'];
    const hasDirectExperimentIntent = hasExperimentWords.some(word => userMessage.includes(word));
    
    console.log("Activation condition check:", {
      userMessageCount,
      scientificContinuity,
      isSpecificTopic,
      hasDirectExperimentIntent,
      analysisResult: analysis
    });
    
    // Stricter activation conditions
    const shouldActivateButtons = (
      userMessageCount >= 2 && // At least 2 turns
      scientificContinuity >= 2 && // At least 2 of last 4 messages are scientific
      isSpecificTopic && // Has specific topic
      analysis.isScientific && // AI judges as scientific
      analysis.experimentable && // Experimentable
      analysis.safetyLevel !== 'dangerous' // Safe
    ) || (
      hasDirectExperimentIntent && // Direct experimental intent expression
      isSpecificTopic && // Specific topic
      analysis.isScientific // Scientific content
    );
    
    if (shouldActivateButtons) {
      setPlanningButtonEnabled(true);
      setReportButtonEnabled(true);
      if (!currentResearchTopic) {
        setCurrentResearchTopic(analysis.topic);
      }
      console.log("✅ Button activation: conditions met");
    } else {
      console.log("⏳ Button deactivation: conditions not met");
    }
    
    // Handle inappropriate topics
    if (!analysis.isScientific && scientificContinuity < 1) {
      const warningContent = `What scientific curiosity do you have today? 🧪

Just ask naturally! People sometimes talk in order, sometimes randomly, right? I like to have conversations that way too 😊

For example:
• "Oh, by the way, why does water boil at 100°C?" 
• "Huh? How do plants make energy from sunlight?"
• "I'm suddenly curious, are the colors we see really all the same colors?"

Express your scientific curiosity naturally like this! ✨`;
      startTypingMessage(warningContent);
      return false;
    }
    
    // Handle dangerous topics - educational approach
    if (analysis.safetyLevel === 'dangerous') {
      console.log("⚠️ Dangerous topic - educational explanation mode");
      
      // Set special flag for dangerous topics
      setCurrentResearchTopic(analysis.topic + " (Theory Only)");
      
      // 🚨 Deactivate buttons (prohibit experimental plan generation)
      setPlanningButtonEnabled(false);
      setReportButtonEnabled(false);
      
      // Continue conversation (allow educational explanation)
      return true;
    }
    
    // If scientific but button activation conditions not met yet
    if (analysis.isScientific && !shouldActivateButtons) {
      if (!currentResearchTopic && isSpecificTopic) {
        setCurrentResearchTopic(analysis.topic);
      }
      
      // Just continue conversation without button guidance if conditions aren't met
      return true; // Continue normal conversation
    }
    
    return true;
  }, [messages, analyzeScientificTopic, startTypingMessage, currentResearchTopic]);

  // Claude API call via Firebase Functions - enhanced retry logic
  const callClaudeAPI = useCallback(async (conversationHistory, userMessage) => {
    const maxRetries = 3;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Check connection status
        if (connectionStatus === 'disconnected' && attempt === 1) {
          await checkConnectionStatus();
        }
        
        // Summarize document content for AI context (extract key sections from HTML)
        const summarizeDocument = (html, maxLength = 800) => {
          if (!html) return null;

          // Remove HTML tags and get text
          const textContent = html
            .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
            .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
            .replace(/<[^>]+>/g, '\n')
            .replace(/&nbsp;/g, ' ')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/\n\s*\n/g, '\n')
            .trim();

          // Extract section headers and brief content
          const lines = textContent.split('\n').filter(line => line.trim());
          let summary = [];
          let currentLength = 0;

          for (const line of lines) {
            const trimmedLine = line.trim();
            if (currentLength + trimmedLine.length > maxLength) break;

            // Prioritize headers and key content
            if (trimmedLine.length > 3) {
              summary.push(trimmedLine);
              currentLength += trimmedLine.length;
            }
          }

          return summary.slice(0, 15).join('\n'); // Max 15 lines
        };

        // Understand current document status
        const documentSummary = summarizeDocument(documentContent);
        const documentStatus = {
          hasResearchPlan: documentMode === 'research-plan' && documentContent,
          hasExperimentReport: documentMode === 'experiment-report' && documentContent,
          isGenerating: isDocumentGenerating,
          currentTopic: currentResearchTopic || 'None',
          isDangerous: currentResearchTopic && currentResearchTopic.includes("(Theory Only)"),
          summary: documentSummary
        };

        // Clean conversation history - remove extra fields that Claude API doesn't accept
        const cleanHistory = conversationHistory.map(msg => ({
          role: msg.role,
          content: msg.content
        }));

        // Natural mode: conversational but service-aware prompt for teachers
        const naturalModePrompt = `You are a supportive science education mentor. Your users are teachers looking for help with student research projects.

**Tone:** Professional but warm. Use polite language. Keep responses natural and conversational, not robotic.

**Current Status:**
- Topic: ${documentStatus.currentTopic}
- Research Plan: ${documentStatus.hasResearchPlan ? 'Generated ✓' : 'Not yet'}
- Experiment Report: ${documentStatus.hasExperimentReport ? 'Generated ✓' : 'Not yet'}
${documentStatus.isDangerous ? '- ⚠️ Safety concern: Focus on theory only, do not suggest experiments.' : ''}

${documentStatus.summary ? `**Generated Document Summary:**
${documentStatus.summary}
` : ''}
**Service Features:**
- Left panel "Competition Projects" button: Search real ISEF database (20,000+ projects)
${documentStatus.isDangerous ? `- ⚠️ Research Plan/Experiment Report buttons are DISABLED for this topic
- Instead, suggest theoretical approaches: simulation, mathematical modeling, literature review, data analysis` : `- Left panel "Research Plan" button: Generate structured research plan
- Left panel "Experiment Report" button: Generate experiment report
- Right panel: View generated documents`}

**Key Rules:**
1. ISEF data is REAL - confirm authenticity when asked ("Yes, this is real competition data")
2. NEVER fabricate ISEF project titles or researcher names - guide users to use the Competition Projects button
3. If a document exists, reference its content when answering questions (e.g., "As shown in your research plan...")
${documentStatus.isDangerous ? `4. ⚠️ CRITICAL: This topic has safety concerns. NEVER mention "Research Plan button" or "Experiment Report button". Instead:
   - Suggest theoretical analysis, simulation, mathematical modeling
   - Guide user to pivot to safer approaches, then buttons will unlock
   - Say "If you'd like to explore a theoretical simulation approach, I can help with that - and that would allow document generation"` : '4. Feel free to suggest using Research Plan or Experiment Report buttons when appropriate'}

Keep responses helpful and concise.`;

        // Classic mode: detailed structured prompt
        const classicModePrompt = `You are a friendly and enthusiastic AI tutor in charge of science education. Your goal is to naturally engage with students and foster scientific curiosity.

**🔥 IMPORTANT: Excel competition database related response rules**
When users ask "Is this real?", "Is this actual data?", "Is this fake?" etc. about data authenticity:

✅ Accurate response:
"Yes! That's correct! 😊

These search results are real data from our Excel competition database.

- ISEF International Science Fair project information
- Actual researcher names, project titles, award records

All are actual projects submitted to real competitions!
You can use this as reference material! 📊✨"

❌ Never give these responses:
- "It might be fake"
- "I'm not sure"
- "Sample data for demonstration"
- Any expression that makes users doubt

**📋 Current document status recognition:**
- Research plan generated: ${documentStatus.hasResearchPlan ? 'YES' : 'NO'}
- Experiment report generated: ${documentStatus.hasExperimentReport ? 'YES' : 'NO'}
- Document generating: ${documentStatus.isGenerating ? 'YES' : 'NO'}
- Current research topic: ${documentStatus.currentTopic}
- Dangerous topic status: ${documentStatus.isDangerous ? 'YES - Theory explanation only' : 'NO'}

🔧 Dangerous topic handling method:
${documentStatus.isDangerous ? `
**⚠️ Currently a dangerous topic:**
- Explain the scientific principles and theories of the topic in detail
- Specifically explain why it's dangerous
- Don't recommend actual experiments, but provide sufficient educational value
- Present safe alternative experiments or related safe topics
- If they say "I want to experiment", guide them to "let's understand it theoretically only"
- Absolutely never provide experimental methods or specific procedures
- 🚨 Very important: Never mention "experiment plan", "research plan", "button", "left side", "design" etc. related to experimental plan generation

Response structure:
1. Explain scientific principles of the topic of interest ✅
2. Clearly explain risk factors and reasons ✅
3. Present safe alternatives or related topics ✅
4. Guide "If you want to know more, let's approach it theoretically" ✅
5. 🚨 Never mention experimental plan related things ❌
` : `
**✅ Safe topic:**
- Freely answer questions and suggest experimental methods
- Can generate research plans or reports
- Provide specific and practical advice
`}

**🚨 CRITICAL ANTI-FABRICATION RULES - NEVER VIOLATE:**
1. NEVER make up, fabricate, or invent any ISEF project titles, researcher names, statistics, or study results
2. When users ask about ISEF projects or science fair examples:
   - DO NOT generate fake project titles like "Biochar Enhancement of Soil Quality" or made-up researcher names
   - Instead say: "You can search real ISEF projects using the 'Competition Projects' button on the left panel. It has verified data from actual competitions."
3. If you don't know specific project information, say so honestly
4. Only mention project titles or researcher names that you are 100% certain are real

Conversation style (important - like natural lecture material):
- Use friendly and comfortable speech (casual tone, emoji use)
- Answer user questions directly (no evasion)
- Prioritize educational value
- Naturally instill safety awareness

**⚠️ Very important document status-specific response rules:**

**📋 When research plan already generated:**
- Never mention "click research plan button on the left" etc.
- Instead use "check the research plan on the right" or "check [specific section] in the document"
- Directly explain document content or provide supplementary explanations
- Answer specifically about additional questions

**📊 When experiment report already generated:**
- For report-related questions, guide "you can check it in the experiment report on the right"
- Provide report content interpretation or additional explanations

**📝 Only when no document exists and it's a safe topic:**
Include "**🎯 Click the research plan button on the left!**" when detecting these patterns:

**🚨 Important: Never mention experiment plan buttons for dangerous topics (Theory Only)**

**Pattern 1: Experimental action intent detection**
- "want to experiment", "going to try", "want to make", "want to measure", "want to observe" etc. action intentions
- "how to do", "what method", "what materials", "what to prepare", "what procedure" etc. method questions

**Pattern 2: Conversation flow analysis**
- Previous turn mentioned science topic + current turn asks specific details
- User asks about same experiment topic 2 or more consecutive times
- Specific questions about experiment materials, procedures, results

**Pattern 3: Experiment stage progression detection**
- Right after I explained experimental methods or materials
- After presenting multiple experiment options and user selected one
- When moving from experimental principles/theory explanation to execution stage

**Pattern 4: Concretization signal**
- When conversation develops from abstract curiosity → specific execution plans
- Expressions like "should I really try this", "is this actually possible?", "can I do this at home?"

**⚠️ Prohibitions for dangerous topic responses:**
- Never mention "experiment plan button", "research plan", "left button" etc. experimental plan generation
- Instead end with "let's approach this theoretically", "let's think about safe alternatives"

${documentStatus.summary ? `**📄 Generated Document Summary (use this to answer questions about the document):**
${documentStatus.summary}
` : ''}
Consider the current conversation context and document status to provide natural and educational responses.`;

        // Select prompt based on mode
        const systemPrompt = aiMode === 'natural' ? naturalModePrompt : classicModePrompt;

        const fullHistory = [
          {
            role: "user",
            content: systemPrompt
          },
          ...cleanHistory,
          { role: "user", content: userMessage }
        ];

        const callClaude = httpsCallable(functions, 'callClaude');
        const response = await callClaude({
          messages: fullHistory,
          max_tokens: 1000,
          accessCode: sessionInfo?.code || '',
          validDays: licenseInfo?.validDays || (sessionInfo?.code === '0000' ? 0 : 30)
        });

        const data = response.data;

        // Check if limit exceeded
        if (data.limitExceeded) {
          return `⚠️ **Chat Limit Reached**\n\n${data.message}\n\nYou can still:\n• Generate documents (if available)\n• Search the ISEF database\n• Upgrade your plan for more chats\n\nCurrent usage: ${data.usage?.chatCount}/${data.usage?.chatLimit} chats`;
        }

        // Update usage info if available
        if (data.usage_info) {
          setApiUsage(data.usage_info);
        }

        setTokenUsage(prev => ({
          ...prev,
          apiCallCount: prev.apiCallCount + 1
        }));

        setConnectionStatus('connected'); // Update connection status on success
        return data.content[0].text;
        
      } catch (error) {
        console.error(`Claude API call attempt ${attempt} failed:`, error);
        
        if (attempt === maxRetries) {
          setConnectionStatus('disconnected');
          return generateFallbackResponse(userMessage);
        }
        
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, attempt * 1000));
        setConnectionStatus('unstable');
      }
    }
  }, [connectionStatus, checkConnectionStatus, documentMode, documentContent, isDocumentGenerating, currentResearchTopic, sessionInfo, licenseInfo, aiMode]);

  // Network error fallback document generation - MODIFIED VERSION
  const generateNetworkErrorDocument = useCallback((type) => {
    const topic = currentResearchTopic || 'Science Experiment';
    
    // Network error message instead of hardcoded content
    return `
<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 30px; line-height: 1.6; color: #1f2937; background: #ffffff; max-width: 100%; word-wrap: break-word; min-height: 50vh; display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center;">
  
  <!-- Error Icon -->
  <div style="margin-bottom: 30px;">
    <div style="display: inline-block; padding: 20px; background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%); border-radius: 50%; margin-bottom: 20px; border: 3px solid #fca5a5;">
      <span style="font-size: 48px; display: block;">⚠️</span>
    </div>
  </div>
  
  <!-- Error Title -->
  <h1 style="color: #dc2626; font-size: 28px; font-weight: 700; margin: 0 0 20px 0;">
    ${type === 'research-plan' ? '🔬 Research Plan' : '📋 Experiment Report'} Generation Failed
  </h1>
  
  <!-- Error Message -->
  <div style="background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%); padding: 25px; border-radius: 12px; border-left: 6px solid #dc2626; max-width: 600px; margin-bottom: 30px;">
    <h3 style="margin: 0 0 15px 0; color: #dc2626; font-size: 20px; font-weight: 600;">Network Connection Problem</h3>
    <p style="margin: 0 0 15px 0; color: #374151; font-size: 16px; line-height: 1.6;">
      The AI document generation service is currently unavailable. This could be due to:
    </p>
    <ul style="margin: 15px 0; padding-left: 20px; color: #374151; font-size: 14px; text-align: left;">
      <li style="margin-bottom: 8px;"><strong>Network connectivity issues</strong></li>
      <li style="margin-bottom: 8px;"><strong>Server maintenance in progress</strong></li>
      <li style="margin-bottom: 8px;"><strong>Temporary service overload</strong></li>
      <li style="margin-bottom: 8px;"><strong>Firewall or connection restrictions</strong></li>
    </ul>
  </div>
  
  <!-- Solutions -->
  <div style="background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); padding: 25px; border-radius: 12px; border-left: 6px solid #3b82f6; max-width: 600px; margin-bottom: 30px;">
    <h3 style="margin: 0 0 15px 0; color: #1e40af; font-size: 18px; font-weight: 600;">🔧 Try These Solutions:</h3>
    <div style="text-align: left; color: #374151; font-size: 14px; line-height: 1.6;">
      <div style="margin-bottom: 12px;">
        <strong style="color: #1e40af;">1. Check Connection:</strong> Verify your internet connection is stable
      </div>
      <div style="margin-bottom: 12px;">
        <strong style="color: #1e40af;">2. Refresh Page:</strong> Try refreshing your browser or restart the app
      </div>
      <div style="margin-bottom: 12px;">
        <strong style="color: #1e40af;">3. Try Again Later:</strong> Wait a few minutes and attempt generation again
      </div>
      <div style="margin-bottom: 12px;">
        <strong style="color: #1e40af;">4. Contact Support:</strong> If problem persists, contact technical support
      </div>
    </div>
  </div>
  
  <!-- Action Buttons -->
  <div style="display: flex; flex-wrap: wrap; gap: 15px; justify-content: center; max-width: 500px;">
    <div style="padding: 12px 24px; background: #3b82f6; color: white; border-radius: 8px; font-weight: 600; font-size: 14px; cursor: pointer;">
      🔄 Try Generation Again
    </div>
    <div style="padding: 12px 24px; background: #10b981; color: white; border-radius: 8px; font-weight: 600; font-size: 14px; cursor: pointer;">
      💬 Continue Conversation
    </div>
    <div style="padding: 12px 24px; background: #6b7280; color: white; border-radius: 8px; font-weight: 600; font-size: 14px; cursor: pointer;">
      📞 Contact Support
    </div>
  </div>
  
  <!-- Footer -->
  <div style="margin-top: 40px; padding: 20px; background: #f9fafb; border-radius: 8px; max-width: 600px;">
    <p style="margin: 0; color: #6b7280; font-size: 12px; font-style: italic;">
      <strong>Note:</strong> ${type === 'research-plan' ? 'Research plans' : 'Experiment reports'} require real-time AI processing for customized content generation. Please ensure stable internet connection for optimal experience.
    </p>
  </div>
  
</div>
    `;
  }, [currentResearchTopic]);

  // Advanced document generation via Firebase Functions - MODIFIED VERSION (no hardcoded fallback)
  const generateAdvancedDocument = useCallback(async (type) => {
    const topic = currentResearchTopic || 'Science Experiment';
    const conversationContext = messages.slice(-5).map(msg => msg.content).join('\n');

    // Ground BOTH the research plan and the experiment report in REAL Intel ISEF
    // projects (fail-safe: [] on any error)
    let referenceProjects = [];
    try {
      referenceProjects = await getRelevantISEFProjects(topic, 8);
      console.log(`📚 ISEF references matched: ${referenceProjects.length}`);
    } catch (e) {
      console.warn('ISEF retrieval failed:', e);
    }

    // For experiment report, include the existing research plan
    const existingResearchPlan = (type === 'experiment-report' && documentMode === 'research-plan' && documentContent)
      ? documentContent
      : null;

    console.log("📋 Document generation start:", { type, topic, hasResearchPlan: !!existingResearchPlan });

    const maxRetries = 2;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`📡 Firebase Functions call... (attempt ${attempt}/${maxRetries})`);

        const response = await fetch('https://us-central1-little-rnd-experiment.cloudfunctions.net/generateDocument', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: type,
            topic: topic,
            conversationContext: conversationContext,
            existingResearchPlan: existingResearchPlan,
            userLevel: 'high-school',
            accessCode: sessionInfo?.code || '',
            validDays: licenseInfo?.validDays || (sessionInfo?.code === '0000' ? 0 : 30),
            referenceProjects: referenceProjects
          })
        });

        console.log("📊 Response status:", response.status, response.statusText);

        // Check for limit exceeded (429 status)
        if (response.status === 429) {
          const errorData = await response.json();
          if (errorData.limitExceeded) {
            // Update usage display
            if (errorData.usage) {
              setApiUsage(prev => ({
                ...prev,
                docCount: errorData.usage.docCount,
                docLimit: errorData.usage.docLimit
              }));
            }
            throw new Error(`DOC_LIMIT_EXCEEDED: ${errorData.message}`);
          }
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
        }

        const data = await response.json();
        console.log("📄 Response data:", { hasDocument: !!data.document, documentLength: data.document?.length });

        if (!data.document) {
          console.warn("⚠️ Empty document returned, retry scheduled");
          throw new Error('Empty document returned');
        }

        console.log("✅ Firebase Functions success!");

        // Update usage count
        setApiUsage(prev => ({
          ...prev,
          docCount: prev.docCount + 1
        }));

        // Update token usage
        setTokenUsage(prev => ({
          ...prev,
          apiCallCount: prev.apiCallCount + 1,
          generatedDocuments: prev.generatedDocuments + estimateTokens(data.document)
        }));

        return data.document; // Return immediately on success
        
      } catch (error) {
        console.error(`❌ Firebase Functions document generation attempt ${attempt} failed:`, error);
        
        if (attempt === maxRetries) {
          console.log("📄 All attempts failed - throwing error for caller to handle");
          throw new Error(`Document generation failed after ${maxRetries} attempts: ${error.message}`);
        }
        
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
  }, [currentResearchTopic, messages, estimateTokens, documentMode, documentContent, sessionInfo, licenseInfo]);

  // Fallback response generation
  const generateFallbackResponse = useCallback((userInput) => {
    // Network status display addition
    const networkPrefix = connectionStatus === 'disconnected' ? 
      '⚠️ **Connection unstable - Local mode**\n\n' : '';
    
    // Check if currently dangerous topic
    const isDangerous = currentResearchTopic && currentResearchTopic.includes("(Theory Only)");
    
    if (userInput.includes('microplastic')) {
      setCurrentResearchTopic('Microplastic Degradation');
      setPlanningButtonEnabled(true);
      setReportButtonEnabled(true);
      return networkPrefix + 'Oh microplastics! That\'s a really hot issue these days 🔬\n\nYou\'re interested in degradation methods! But you know, to research this properly...\n\nHey, by the way **🎯 The research plan button on the left is activated! Click it!** I\'ll help you design everything from experiments to expected results systematically! 💪';
    } else if (userInput.includes('diode')) {
      setCurrentResearchTopic('Diode Logic Circuits');
      setPlanningButtonEnabled(true);
      setReportButtonEnabled(true);
      return networkPrefix + 'Diode logic circuits! The basics of electronic circuits ⚡\n\nI can see you want to experiment! But to do this properly... hmm, thinking about it\n\n**🎯 The research plan button on the left sidebar is activated!** Click it and I\'ll perfectly design everything from AND, OR gates experiments to result predictions! 🎯';
    } else if (userInput.includes('plant')) {
      setCurrentResearchTopic('Plant Growth Research');
      setPlanningButtonEnabled(true);
      setReportButtonEnabled(true);
      return networkPrefix + 'Plant growth research! The core of life science 🌱\n\nCurious about what conditions help plants grow better? Photosynthesis, nutrients, environmental factors are all related!\n\nIf you want to experiment... ah, rather than just talking about it, let\'s systematically plan the research first! 🌿';
    } else if (userInput.includes('solar')) {
      setCurrentResearchTopic('Solar Energy Research');
      setPlanningButtonEnabled(true);
      setReportButtonEnabled(true);
      return networkPrefix + 'Solar energy! The core technology of the future ☀️\n\nCurious about ways to improve solar cell efficiency or solar power generation principles? It\'s a really interesting field!\n\nThere are many projects you can actually experiment with. Shall we plan a research project? 🔋';
    } else {
      // Remove experimental plan mentions for dangerous topics
      if (isDangerous) {
        return networkPrefix + `Interesting topic! 🤔

The principles related to ${userInput} in science are really fascinating. But for safety, it would be good to approach this theoretically only!

If you have more questions, ask anytime! ✨`;
      } else {
        return networkPrefix + 'Interesting topic! 🤔\n\nAsk me anything curious about science. Let\'s also think together about whether we can verify it through experiments!\n\nBy any chance, what field are you interested in specifically? Chemistry, biology, physics, environmental science... anything works! ✨';
      }
    }
  }, [connectionStatus, currentResearchTopic]);

  const triggerCompetitionSearch = useCallback(async () => {
    setIsLoading(true);
    
    try {
      let response = '';
      
      if (!isExcelLoaded) {
        response = `🔍 **Searching real competition project database for you!**

⏳ **Loading Excel files...**
Loading ISEF International Science Fair data!

${excelLoadError ? `❌ Loading error: ${excelLoadError}` : '📊 Please wait a moment...'}

Once completed, I'll search through actual 20,000+ projects with AI! 🔎

---
📊 Target: ISEF International Science Fair Excel files`;

        startTypingMessage(response);
        setIsLoading(false);
        return;
      }

      if (messages.length <= 2) {
        // Emphasize AI search features in initial guidance
        response = `🔍 **Searching real competition project database for you!**

You can find information about projects that actually won at real ISEF International Science Fair! 📚

🤖 **AI Intelligent Search System:**
- **Keyword expansion** → AI automatically expands search with related terms
- **Relevance scoring** → AI ranks by topic relevance
- **Database search** → Search across ISEF competition database

**Data holdings:**
🌍 ISEF International Science Fair: ${realExcelData.length.toLocaleString()} winning projects

**🔥 AI Search Examples:**
- **"microplastics"** → automatically searches with microplastic, plastic pollution
- **"quantum computing"** → expands search with quantum computing, quantum algorithm
- **"synthetic biology"** → intelligent search with synthetic biology, bioengineering

💡 **How to use:** Simply input scientific terms naturally and AI will automatically search international projects for you!

🔒 **Privacy protection:** Researcher names are masked for display (J*** / A**)

---
🤖 AI supported database ${realExcelData.length.toLocaleString()} projects`;

      } else {
        // Perform actual AI search
        const latestMessage = messages[messages.length - 1].content;      
        // Extract keyword - context priority approach: utilize currentResearchTopic
        const searchKeyword = currentResearchTopic || 
                             latestMessage.split(' ').find(word => word.length > 2) || 
                             'science';        
        // Perform AI search (asynchronous)
        const searchResults = await searchRealExcelData(searchKeyword);
        
        if (searchResults.totalResults === 0) {
          response = `🔍 **"${searchKeyword}" Search Results**

**Search scope:**
- ISEF International Science Fair: ${realExcelData.length.toLocaleString()} projects

**Result:** No projects matching the keyword were found.

---
📊 Search completed`;
        } else {
          // Generate simplified project-centered response
          response = `🔍 **"${searchKeyword}" - ISEF Projects**

Found **${searchResults.results.length}** matching projects from ISEF database.

`;

          // Display ISEF results (simplified format)
          if (searchResults.results.length > 0) {
            response += `🌍 **Intel ISEF International Projects**\n\n`;

            searchResults.results.forEach((item, index) => {
              // Simple name masking: first 5 chars + **
              const maskName = (name) => {
                if (!name || name === 'No author information') return null;
                const cleanName = name.trim();
                if (cleanName.length <= 5) return cleanName;
                return cleanName.substring(0, 5) + '**';
              };

              const maskedAuthor = maskName(item.author);

              response += `**${index + 1}. ${item.title}**\n`;
              response += `📅 ${item.year} | 📂 ${item.category}`;
              if (maskedAuthor) {
                response += ` | 👤 ${maskedAuthor}`;
              }
              if (item.country) {
                response += ` | 🌍 ${item.country}`;
              }
              response += `\n`;

              // Award information
              if (item.awards && item.awards !== 'No awards' && item.awards.trim() !== '') {
                response += `🏆 ${item.awards}\n`;
              }
              response += `\n`;
            });
          } else {
            response += `❌ No matching ISEF projects found.\n`;
          }

          response += `---
💡 **Tip:** Use these as research inspiration. Try different keywords for more results.`;
        }
      }
      
      startTypingMessage(response);
      
    } catch (error) {
      console.error("AI search error:", error);
      
      const fallbackResponse = `❌ **Error occurred during AI search**

There's a temporary issue with the search system.

**Temporary solution:**
- Try searching directly with keywords
- Try again after a moment
- Try again after refresh

---
📊 AI search system temporarily down`;
      
      startTypingMessage(fallbackResponse);
    }
    
    setIsLoading(false);
  }, [messages, isExcelLoaded, realExcelData, excelLoadError, searchRealExcelData, startTypingMessage, currentResearchTopic]);

  // Research plan generation - MODIFIED VERSION
  const triggerResearchPlan = useCallback(async () => {
    // AI-based topic transition detection
    if (currentResearchTopic && currentResearchTopic.includes("(Theory Only)")) {
      const recentMessages = messages.slice(-4).map(msg => msg.content).join(' ');
      const transitionAnalysis = await checkTopicTransition(recentMessages);
      
      if (transitionAnalysis.hasTransitioned) {
        console.log("📄 AI detected: transition to safe alternative experiment!", transitionAnalysis);
        
        // Update to safe topic (remove theory only flag)
        setCurrentResearchTopic(transitionAnalysis.newSafeTopic);
        
        // Reactivate buttons
        setPlanningButtonEnabled(true);
        setReportButtonEnabled(true);
        
        console.log("✅ Topic updated:", transitionAnalysis.newSafeTopic);
      }
    }
    
    // Block generation only for dangerous topics
    if (currentResearchTopic && currentResearchTopic.includes("(Theory Only)")) {
      const safetyMessage = `This topic cannot generate research plans for safety reasons! ⚠️

But theoretical analysis reports or safe alternative experiments are possible!

• Theoretical research report
• Literature review report  
• Safe related experiment design

What direction would you like to approach? 🤔`;
      
      startTypingMessage(safetyMessage);
      return;
    }
    
    setDocumentMode('research-plan');
    setIsDocumentGenerating(true);
    setDocumentProgress(0);
    setDocumentProgressMessage('🔬 Starting research plan generation...');
    
    const messageContent = '🔬 Generating research plan...\n\nCheck the real-time creation in the document window on the right! AI will analyze the conversation context and create a customized research plan for you! ✨\n\n💡 **If you have any questions while viewing the document, ask me anytime!** For example:\n• "I don\'t understand step 2 of the experiment method, explain in more detail"\n• "Are there alternative materials for the supplies?"\n• "What else should I be careful about in this experiment?"\n\nThe more specific your questions, the more detailed answers you can get!';
    startTypingMessage(messageContent);
    
    // Progress steps
    const progressSteps = [
      { progress: 3, message: '📚 Analyzing conversation context...', time: 4800 },
      { progress: 8, message: '🎯 Setting research objectives...', time: 6000 },
      { progress: 15, message: '🔬 Configuring experiment design...', time: 5000 },
      { progress: 23, message: '📊 Establishing data analysis plan...', time: 5200 },
      { progress: 32, message: '📝 Organizing materials and supplies...', time: 5000 },
      { progress: 42, message: '⚗️ Detailing experimental procedures...', time: 5500 },
      { progress: 52, message: '📈 Analyzing expected results...', time: 5000 },
      { progress: 62, message: '📋 Reviewing safety guidelines...', time: 4000 },
      { progress: 71, message: '📄 Starting document structuring...', time: 5600 },
      { progress: 79, message: '🎨 Applying HTML styling...', time: 8000 },
      { progress: 86, message: '✨ Verifying and correcting content...', time: 5200 },
      { progress: 92, message: '🔧 Final formatting...', time: 8000 },
      { progress: 97, message: '🎯 Final quality review...', time: 6000 }
    ];
    
    let stepIndex = 0;
    const executeProgressStep = () => {
      if (stepIndex < progressSteps.length) {
        const currentStep = progressSteps[stepIndex];
        setDocumentProgress(currentStep.progress);
        setDocumentProgressMessage(currentStep.message);
        stepIndex++;
        
        if (stepIndex < progressSteps.length) {
          setTimeout(executeProgressStep, currentStep.time);
        }
      }
    };

    executeProgressStep();
    
    try {
      // API로만 문서 생성 시도
      const document = await generateAdvancedDocument('research-plan');
      setDocumentProgress(100);
      setDocumentProgressMessage('✅ Research plan generation complete!');
      setTimeout(() => {
        setDocumentContent(document);
        setIsDocumentGenerating(false);
      }, 500);
    } catch (error) {
      console.error('Research plan generation failed:', error);
      
      // 네트워크 오류 메시지 표시
      const errorDocument = generateNetworkErrorDocument('research-plan');
      setDocumentProgress(100);
      setDocumentProgressMessage('❌ Generation failed - Network error');
      
      setTimeout(() => {
        setDocumentContent(errorDocument);
        setIsDocumentGenerating(false);
      }, 500);
      
      // 채팅에도 오류 메시지 추가
      const errorMessage = {
        role: 'assistant',
        content: `❌ **Research Plan Generation Failed**

Network connection problem occurred during document generation.

**Possible causes:**
• Internet connection instability
• Server maintenance in progress  
• Temporary service overload

**Solutions:**
• Check your internet connection
• Refresh the page and try again
• Wait a few minutes and retry
• Contact support if the problem persists

You can continue our conversation while we resolve this issue! 💬`,
        timestamp: new Date().toLocaleTimeString(),
        isComplete: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
    }
    
    setReportButtonEnabled(true);
  }, [currentResearchTopic, messages, checkTopicTransition, startTypingMessage, generateAdvancedDocument, generateNetworkErrorDocument]);

  // Experiment report generation - MODIFIED VERSION
  const triggerExperimentReport = useCallback(async () => {
    // AI-based topic transition detection
    if (currentResearchTopic && currentResearchTopic.includes("(Theory Only)")) {
      const recentMessages = messages.slice(-4).map(msg => msg.content).join(' ');
      const transitionAnalysis = await checkTopicTransition(recentMessages);
      
      if (transitionAnalysis.hasTransitioned) {
        console.log("📄 AI detected: transition to safe alternative experiment! (report)", transitionAnalysis);
        
        // Update to safe topic (remove theory only flag)
        setCurrentResearchTopic(transitionAnalysis.newSafeTopic);
        
        // Reactivate buttons
        setPlanningButtonEnabled(true);
        setReportButtonEnabled(true);
        
        console.log("✅ Topic updated (report):", transitionAnalysis.newSafeTopic);
      }
    }
    
    // Block generation only for dangerous topics  
    if (currentResearchTopic && currentResearchTopic.includes("(Theory Only)")) {
      const safetyMessage = `For safety reasons, experiment reports for this topic cannot be generated! 🛡️

But theoretical analysis reports or safe alternative experiments are possible!

• Theoretical research report
• Literature review report  
• Safe related experiment design

What format would you like to organize it in? 📚`;
      
      startTypingMessage(safetyMessage);
      return;
    }
    
    setDocumentMode('experiment-report');
    setIsDocumentGenerating(true);
    setDocumentProgress(0);
    setDocumentProgressMessage('📋 Starting experiment report generation...');
    
    const messageContent = '📋 Generating experiment report...\n\nCheck the report being completed with virtual experiment data in the document window on the right! I\'ll write it in the form of an actual scientific paper! 📊\n\n💡 **If any part of the report content is difficult to understand, ask right away!** For example:\n• "What does the 95.7% change rate in the experiment results mean?"\n• "Explain the conclusion section more simply"\n• "What should I be careful about if I actually do this experiment?"\n\nThe more specifically you ask, the more accurate and detailed explanations you can receive!';
    startTypingMessage(messageContent);
    
    // Progress steps for experiment report
    const progressSteps = [
      { progress: 2, message: '🎯 Analyzing experiment objectives...', time: 5000 },
      { progress: 6, message: '🧪 Configuring experiment methods...', time: 3800 },
      { progress: 12, message: '📊 Preparing virtual data generation...', time: 3600 },
      { progress: 19, message: '🔢 Calculating experiment data...', time: 4400 },
      { progress: 27, message: '📈 Starting result analysis...', time: 5000 },
      { progress: 36, message: '📊 Generating graphs and charts...', time: 5000 },
      { progress: 45, message: '💡 Writing conclusions and discussion...', time: 5600 },
      { progress: 54, message: '📚 Organizing references...', time: 5600 },
      { progress: 63, message: '📋 Reviewing error analysis...', time: 4800 },
      { progress: 72, message: '📄 Structuring report...', time: 6400 },
      { progress: 80, message: '🎨 Applying document formatting...', time: 5600 },
      { progress: 87, message: '✨ Verifying and correcting content...', time: 7200 },
      { progress: 93, message: '🔧 Final quality review...', time: 7400 },
      { progress: 98, message: '🎯 Finishing work...', time: 3000 }
    ];
    
    let stepIndex = 0;
    const executeProgressStep = () => {
      if (stepIndex < progressSteps.length) {
        const currentStep = progressSteps[stepIndex];
        setDocumentProgress(currentStep.progress);
        setDocumentProgressMessage(currentStep.message);
        stepIndex++;
        
        if (stepIndex < progressSteps.length) {
          setTimeout(executeProgressStep, currentStep.time);
        }
      }
    };
    
    executeProgressStep();
    
    try {
      const document = await generateAdvancedDocument('experiment-report');
      setDocumentProgress(100);
      setDocumentProgressMessage('✅ Experiment report generation complete!');
      setTimeout(() => {
        setDocumentContent(document);
        setIsDocumentGenerating(false);
      }, 1000);
    } catch (error) {
      console.error('Experiment report generation failed:', error);
      
      // 네트워크 오류 메시지 표시
      const errorDocument = generateNetworkErrorDocument('experiment-report');
      setDocumentProgress(100);
      setDocumentProgressMessage('❌ Generation failed - Network error');
      
      setTimeout(() => {
        setDocumentContent(errorDocument);
        setIsDocumentGenerating(false);
      }, 1000);
      
      // 채팅에도 오류 메시지 추가
      const errorMessage = {
        role: 'assistant',
        content: `❌ **Experiment Report Generation Failed**

Network connection problem occurred during document generation.

**Possible causes:**
• Internet connection instability
• Server maintenance in progress
• Temporary service overload

**Solutions:**
• Check your internet connection
• Refresh the page and try again
• Wait a few minutes and retry
• Contact support if the problem persists

Feel free to continue our conversation in the meantime! 💬`,
        timestamp: new Date().toLocaleTimeString(),
        isComplete: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
    }
  }, [currentResearchTopic, messages, checkTopicTransition, startTypingMessage, generateAdvancedDocument, generateNetworkErrorDocument]);
  
  // PDF download function - 레퍼런스 정확히 복사 (셀렉터만 수정)
  const handlePDFDownload = useCallback(() => {
  console.log("📄 PDF 다운로드 시작 - 자연스러운 페이지 플로우");
  
  // 1. 페이지 브레이크 제거한 프린트 스타일
  const printStyle = document.createElement('style');
  printStyle.id = 'pdf-print-style';
  printStyle.textContent = `
    @page {
      size: A4;
      margin: 10mm;
    }
    
    @media print {
      * {
        box-sizing: border-box !important;
      }
      
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        background: white !important;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
        font-size: 12pt !important;  /* 적당한 크기 유지 */
        line-height: 1.4 !important;  /* 읽기 좋은 줄간격 유지 */
        color: #000 !important;
      }
      
      /* 숨길 요소들 */
      .pdf-hide-sidebar,
      .pdf-hide-chat,
      header, nav, footer,
      [class*="sidebar"], [class*="menu"],
      button, input, textarea {
        display: none !important;
        visibility: hidden !important;
        position: absolute !important;
        left: -9999px !important;
        width: 0 !important;
        height: 0 !important;
        overflow: hidden !important;
      }
      
      /* 문서 영역 */
      .pdf-full-document {
        display: block !important;
        visibility: visible !important;
        width: 100% !important;
        max-width: 100% !important;
        height: auto !important;
        position: static !important;
        border: none !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: visible !important;
        background: white !important;
      }
      
      .pdf-full-document > div {
        padding: 0 !important;
        margin: 0 !important;
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
      }
      
      .pdf-full-document * {
        max-width: 100% !important;
        overflow: visible !important;
        height: auto !important;
        max-height: none !important;
      }
      
      /* 제목 스타일 - 페이지 브레이크 제거 */
      .pdf-full-document h1,
      .pdf-full-document h2,
      .pdf-full-document h3,
      .pdf-full-document h4,
      .pdf-full-document h5,
      .pdf-full-document h6 {
        color: #1f2937 !important;
        margin: 8pt 0 4pt 0 !important;
        padding: 0 !important;
        page-break-before: auto !important;  /* avoid → auto */
        page-break-after: auto !important;   /* avoid → auto */
        page-break-inside: auto !important;  /* avoid → auto */
        font-weight: bold !important;
      }
      
      .pdf-full-document h1 { font-size: 18pt !important; }
      .pdf-full-document h2 { font-size: 16pt !important; }
      .pdf-full-document h3 { font-size: 14pt !important; }
      
      /* 문단 스타일 - 자연스러운 플로우 */
      .pdf-full-document p {
        margin: 4pt 0 !important;
        padding: 0 !important;
        text-align: left !important;
        page-break-before: auto !important;
        page-break-after: auto !important;
        page-break-inside: auto !important;  /* 문단이 페이지 경계에서 나뉠 수 있음 */
      }
      
      /* 리스트 스타일 - 페이지 브레이크 허용 */
      .pdf-full-document ul,
      .pdf-full-document ol {
        margin: 4pt 0 4pt 15pt !important;
        padding: 0 !important;
        page-break-before: auto !important;
        page-break-after: auto !important;
        page-break-inside: auto !important;  /* 리스트가 페이지 경계에서 나뉠 수 있음 */
      }
      
      .pdf-full-document li {
        margin: 2pt 0 !important;
        padding: 0 !important;
        page-break-before: auto !important;
        page-break-after: auto !important;
        page-break-inside: auto !important;
      }
      
      /* 테이블 스타일 - 작은 테이블은 분할 허용 */
      .pdf-full-document table {
        width: 100% !important;
        border-collapse: collapse !important;
        margin: 6pt 0 !important;
        page-break-before: auto !important;
        page-break-after: auto !important;
        page-break-inside: auto !important;  /* 테이블도 나뉠 수 있음 */
      }
      
      .pdf-full-document th,
      .pdf-full-document td {
        border: 1pt solid #000 !important;
        padding: 4pt 6pt !important;
        text-align: left !important;
        page-break-inside: auto !important;
      }
      
      .pdf-full-document th {
        background-color: #f5f5f5 !important;
        font-weight: bold !important;
      }
      
      /* div 박스/카드 스타일 - 페이지 브레이크 허용 */
      .pdf-full-document div[style*="background"],
      .pdf-full-document div[style*="border"],
      .pdf-full-document div[class*="bg-"],
      .pdf-full-document div[class*="border"] {
        border: 1pt solid #ddd !important;
        padding: 6pt !important;
        margin: 4pt 0 !important;
        background: #f9f9f9 !important;
        page-break-before: auto !important;
        page-break-after: auto !important;
        page-break-inside: auto !important;  /* 박스도 나뉠 수 있음 */
      }
      
      /* 모든 div - 페이지 브레이크 자유롭게 */
      .pdf-full-document div {
        margin: 0 !important;
        padding: 2pt 0 !important;
        page-break-before: auto !important;
        page-break-after: auto !important;
        page-break-inside: auto !important;
      }
      
      /* 섹션별 구분은 유지하되 페이지 브레이크는 자연스럽게 */
      .pdf-full-document section,
      .pdf-full-document article {
        page-break-before: auto !important;
        page-break-after: auto !important;
        page-break-inside: auto !important;
        margin: 6pt 0 !important;
      }
      
      /* 모든 요소의 페이지 브레이크를 자동으로 */
      .pdf-full-document * {
        page-break-before: auto !important;
        page-break-after: auto !important;
        page-break-inside: auto !important;
        orphans: 2 !important;  /* 페이지 하단에 최소 2줄 */
        widows: 2 !important;   /* 페이지 상단에 최소 2줄 */
      }
      
      /* 색상 보정 */
      .pdf-full-document * {
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      
      /* 링크 스타일 */
      .pdf-full-document a {
        color: #000 !important;
        text-decoration: underline !important;
      }
      
      /* 이미지 - 자연스럽게 분할 허용 */
      .pdf-full-document img {
        max-width: 100% !important;
        height: auto !important;
        page-break-inside: auto !important;
      }
      
      /* 첫 번째와 마지막 요소 여백 제거 */
      .pdf-full-document > div:first-child {
        margin-top: 0 !important;
        padding-top: 0 !important;
      }
      
      .pdf-full-document > div:last-child {
        margin-bottom: 0 !important;
        padding-bottom: 0 !important;
      }
    }
    
    @media screen {
      .pdf-hide-sidebar { display: none !important; }
      .pdf-hide-chat { display: none !important; }
      .pdf-full-document { 
        width: 100vw !important; 
        height: 100vh !important;
        position: fixed !important; 
        top: 0 !important; 
        left: 0 !important; 
        z-index: 9999 !important;
        border: none !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: auto !important;
        background: white !important;
      }
    }
  `;
  document.head.appendChild(printStyle);
  
  // 2. DOM 요소 찾기
  const sidebar = document.querySelector('div.w-80') || 
                  document.querySelector('div[class*="w-80"]') ||
                  document.querySelector('div[class*="transform transition-transform"]');
  
  const chatArea = document.querySelector('div[class*="flex-1 flex flex-col"]:not([class*="border-l"])') ||
                   document.querySelector('div[class*="lg:w-1/2"]:not([class*="border-l"])');
  
  const docArea = document.querySelector('div[class*="bg-white border-l border-gray-200 flex-col"]') ||
                  document.querySelector('div[class*="border-l border-gray-200"]') ||
                  document.querySelector('div[class*="bg-white border-l"]');
  
  console.log("찾은 요소들:", { sidebar: !!sidebar, chatArea: !!chatArea, docArea: !!docArea });
  
  if (!docArea) {
    alert('문서 영역을 찾을 수 없습니다. 문서가 생성되었는지 확인해주세요.');
    printStyle.remove();
    return;
  }
  
  // 3. 클래스 적용
  if (sidebar) {
    sidebar.classList.add('pdf-hide-sidebar');
    console.log("✅ 사이드바 숨김");
  }
  
  if (chatArea) {
    chatArea.classList.add('pdf-hide-chat');
    console.log("✅ 채팅 영역 숨김");
  }
  
  if (docArea) {
    docArea.classList.add('pdf-full-document');
    console.log("✅ 문서 영역 전체화면");
  }
  
  // 4. 스크롤 컨테이너 해제
  const scrollContainers = document.querySelectorAll('.overflow-y-auto, [style*="overflow-y"], [style*="max-height"]');
  const originalStyles = [];
  
  scrollContainers.forEach((container, index) => {
    originalStyles[index] = {
      overflow: container.style.overflow,
      overflowY: container.style.overflowY,
      height: container.style.height,
      maxHeight: container.style.maxHeight
    };
    
    container.style.overflow = 'visible';
    container.style.overflowY = 'visible';
    container.style.height = 'auto';
    container.style.maxHeight = 'none';
  });
  
  console.log(`스크롤 컨테이너 ${scrollContainers.length}개 수정`);
  
  // 5. 프린트 실행
  setTimeout(() => {
    console.log("🖨️ 프린트 실행!");
    window.print();
    
    // 6. 복구
    setTimeout(() => {
      console.log("🔄 원래 상태로 복구 중...");
      
      if (sidebar) sidebar.classList.remove('pdf-hide-sidebar');
      if (chatArea) chatArea.classList.remove('pdf-hide-chat');
      if (docArea) docArea.classList.remove('pdf-full-document');
      
      scrollContainers.forEach((container, index) => {
        const original = originalStyles[index];
        container.style.overflow = original.overflow || '';
        container.style.overflowY = original.overflowY || '';
        container.style.height = original.height || '';
        container.style.maxHeight = original.maxHeight || '';
      });
      
      if (printStyle) printStyle.remove();
      
      console.log("✅ 복구 완료!");
    }, 1500);
  }, 1500);
}, []);
  
  // Close document function - enhanced for mobile
  const handleCloseDocument = useCallback(() => {
    setDocumentContent('');
    setDocumentMode(null);
    setIsDocumentGenerating(false);
    setDocumentProgress(0);
    setDocumentProgressMessage('');
    setIsDocumentFullscreen(false); // Reset fullscreen mode
  }, []);

  // Toggle sidebar for mobile
  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev);
  }, []);

  // Toggle document fullscreen for mobile
  const toggleDocumentFullscreen = useCallback(() => {
    setIsDocumentFullscreen(prev => !prev);
  }, []);

  // Close sidebar when clicking outside (mobile)
  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  // Message handling functions
  const handleSendMessage = useCallback(async () => {
  if (!inputValue.trim() || isLoading) return;

  const userMessage = inputValue.trim();
  setInputValue('');
  setIsLoading(true);
  setCanStop(true);

  // Add user message
  const newUserMessage = {
    role: 'user',
    content: userMessage,
    timestamp: new Date().toLocaleTimeString(),
    isComplete: true
  };
  setMessages(prev => [...prev, newUserMessage]);

  // Check token limit - if exceeded, show warning instead of AI response
  // Include the new message in token calculation (setMessages is async)
  const currentTokens = getCurrentTokens() + estimateTokens(userMessage);
  if (currentTokens >= maxTokens) {
    const limitWarning = `⚠️ **Conversation limit reached.**

Your conversation has exceeded the maximum context length. To continue using the AI assistant effectively, please start a new conversation.

**How to start over:**
1. Click the **"Start Over"** button in the left panel, or
2. Click the **"Reset"** button at the top of the chat

Your generated documents are saved and won't be lost. Starting fresh ensures the best AI response quality.`;

    startTypingMessage(limitWarning);
    setIsLoading(false);
    setCanStop(false);
    return;
  }

  try {
    // Analyze topic and update trigger states
    const shouldContinue = await updateTriggerStates(userMessage);
    if (!shouldContinue) {
      setIsLoading(false);
      setCanStop(false);
      return;
    }

    // Get AI response
    const aiResponse = await callClaudeAPI(messages, userMessage);
    
    // Add AI response with typing animation
    startTypingMessage(aiResponse);
    
  } catch (error) {
    console.error('Message handling error:', error);
    const fallbackResponse = generateFallbackResponse(userMessage);
    startTypingMessage(fallbackResponse);
  }
  
  setIsLoading(false);
}, [inputValue, isLoading, messages, updateTriggerStates, callClaudeAPI, startTypingMessage, generateFallbackResponse, getCurrentTokens, maxTokens, estimateTokens]);
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  // Stop generation function
  const handleStopGeneration = useCallback(() => {
    setIsLoading(false);
    setCanStop(false);
    
    // Stop typing animation
    if (typingMessageIndex !== null) {
      setMessages(prev => prev.map((msg, index) => 
        index === typingMessageIndex ? { 
          ...msg, 
          content: displayedText + '\n\n⏹️ **Response stopped by user**',
          isComplete: true 
        } : msg
      ));
      setTypingMessageIndex(null);
      setDisplayedText('');
      setCanStop(false);
    }
    
    // Add stop message
    const stopMessage = {
      role: 'assistant',
      content: '⏹️ Response was stopped. Feel free to ask something new! 🤔',
      timestamp: new Date().toLocaleTimeString(),
      isComplete: true
    };
    
    setMessages(prev => [...prev, stopMessage]);
  }, [typingMessageIndex, displayedText]);

  // ===== useEffect hooks =====
  
  // Check IP license and stored session on mount
  useEffect(() => {
    const checkStoredSession = async () => {
      // Step 1: Check IP-based license first (for schools/organizations)
      try {
        console.log('🏫 Checking IP-based license...');
        const checkIPLicense = httpsCallable(functions, 'checkIPLicense');
        const ipResult = await checkIPLicense({});

        if (ipResult.data.success && ipResult.data.hasLicense) {
          console.log('✅ IP license found!', ipResult.data.licenseInfo);

          // Create session data from IP license
          const ipLicenseInfo = ipResult.data.licenseInfo;
          const now = new Date();
          const expireDate = ipLicenseInfo.expiresAt ?
            new Date(ipLicenseInfo.expiresAt) :
            new Date(now.getTime() + (ipLicenseInfo.validDays || 365) * 24 * 60 * 60 * 1000);

          const sessionData = {
            code: 'IP_LICENSE',  // 세션 체크 시 식별용
            accessType: 'premium',
            productName: ipLicenseInfo.productName || 'School License',
            organization: ipLicenseInfo.organization,
            expireDate: expireDate.toISOString(),
            startDate: now.toISOString(),
            remainingTime: Math.floor((expireDate - now) / 1000),
            validDays: ipLicenseInfo.validDays || 365,
            duration_days: ipLicenseInfo.validDays || 365,  // processLicenseInfo 호환용 (1년)
            isIPLicense: true
          };

          // Save to localStorage
          localStorage.setItem('accessSession', JSON.stringify({
            sessionData,
            licenseType: 'ip',
            organization: ipLicenseInfo.organization
          }));

          // Set API usage limits from IP license
          setApiUsage({
            chatCount: 0,
            docCount: 0,
            chatLimit: ipLicenseInfo.chatLimit || 2000,
            docLimit: ipLicenseInfo.docLimit || 100,
            productName: ipLicenseInfo.productName || 'School License'
          });

          // Process license info and authenticate
          const processedInfo = processLicenseInfo(sessionData);
          setLicenseInfo(processedInfo);
          setIsAuthenticated(true);
          setSessionInfo({
            code: 'IP_LICENSE',
            organization: ipLicenseInfo.organization,
            ...processedInfo
          });
          setUserStatus('premium');

          console.log('🏫 IP license auto-login successful:', ipLicenseInfo.organization);
          return; // Skip normal session check
        } else {
          console.log('📝 No IP license found, proceeding with normal check');
        }
      } catch (error) {
        console.log('📝 IP license check skipped (error or not available):', error.message);
      }

      // Step 2: Fall back to stored session check
      const storedSessionData = getStoredSessionData();
      if (storedSessionData) {
        console.log('📄 Found stored session, verifying...', storedSessionData);

        try {
          const result = await checkLicenseValidity(storedSessionData.code || '0000');
          if (result.isValid) {
            setIsAuthenticated(true);
            setSessionInfo({
              code: storedSessionData.code || '0000',
              ...result.licenseInfo
            });
            console.log('✅ Stored session valid, auto-login successful');
          } else {
            console.log('❌ Stored session invalid, cleanup required');
            localStorage.removeItem('accessSession');
            localStorage.removeItem('sessionData');
          }
        } catch (error) {
          console.error('Session verification error:', error);
        }
      }
    };

    checkStoredSession();
  }, []);

  // Mobile device detection
  useEffect(() => {
    const checkMobileDevice = () => {
      const isMobile = window.innerWidth < 768; // Tablets are 768px and above
      setIsMobileDevice(isMobile);
      if (isMobile) {
        setShowMobileWarning(true);
      }
    };

    checkMobileDevice();
    window.addEventListener('resize', checkMobileDevice);

    return () => window.removeEventListener('resize', checkMobileDevice);
  }, []);

  // Load Excel data on mount
  useEffect(() => {
    if (!isExcelLoaded) {
      loadRealExcelData();
    }
  }, [isExcelLoaded, loadRealExcelData]);

  // Timer countdown effect
  useEffect(() => {
    let interval;
    
    if (isTimerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(prev => {
          const newTime = prev - 1;
          if (newTime <= 0) {
            handleTrialExpired();
            return 0;
          }
          return newTime;
        });
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerActive, timeLeft, handleTrialExpired]);

  // Session check interval
  useEffect(() => {
    let interval;
    
    if (isAuthenticated && sessionInfo) {
      // Check session every 30 seconds
      interval = setInterval(() => {
        checkSession();
      }, 30000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuthenticated, sessionInfo, checkSession]);

  // Typing animation effect
  useEffect(() => {
    if (typingMessageIndex !== null && messages[typingMessageIndex] && !messages[typingMessageIndex].isComplete) {
      const fullText = messages[typingMessageIndex].content;
      const currentLength = displayedText.length;
      
      if (currentLength < fullText.length) {
        const timer = setTimeout(() => {
          setDisplayedText(fullText.slice(0, currentLength + 1));
          scrollToBottom();
        }, 18);
        
        return () => clearTimeout(timer);
      } else {
        // Animation complete
        setMessages(prev => prev.map((msg, index) => 
          index === typingMessageIndex ? { ...msg, isComplete: true } : msg
        ));
        setTypingMessageIndex(null);
        setDisplayedText('');
      }
    }
  }, [typingMessageIndex, displayedText, messages, scrollToBottom]);

  // Auto scroll effect
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // ===== Render functions =====
  
  // Login screen render
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 w-full max-w-lg">
          {/* Mobile Warning */}
          {isMobileDevice && showMobileWarning && (
            <div className="mb-4 bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-semibold text-amber-800 mb-1">
                    📱 Mobile Device Detected
                  </h3>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    This service is optimized for <strong>tablet and desktop/laptop</strong> devices. For the best experience, please access from a larger screen device.
                  </p>
                  <button
                    onClick={() => setShowMobileWarning(false)}
                    className="mt-2 text-xs text-amber-800 hover:text-amber-900 underline font-medium"
                  >
                    Continue anyway
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="bg-blue-100 p-4 rounded-full">
                <Beaker className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Little Science AI Chat</h1>
            <p className="text-gray-600 text-sm sm:text-base">Science Research Design AI Tutor</p>
          </div>

          {/* Access Code Input Section */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Enter Access Code</h2>
            <div className="space-y-4">
              <div>
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  placeholder="Enter your code"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-base"
                  onKeyPress={(e) => e.key === 'Enter' && verifyAccessCode(accessCode)}
                  disabled={isVerifying}
                />
              </div>
              
              {authError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <span className="text-red-700 text-sm">{authError}</span>
                </div>
              )}
              
              <button
                onClick={() => verifyAccessCode(accessCode)}
                disabled={isVerifying || !accessCode.trim()}
                className="w-full bg-gray-400 text-white py-3 px-4 rounded-lg hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
              >
                {isVerifying ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Connecting...</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    <span>Access</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* What You Can Do Section */}
          <div className="mb-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-800 mb-2">
              What You Can Do
            </h3>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• AI-powered research proposal generation</li>
              <li>• Lab report creation with data analysis</li>
              <li>• 20,000+ ISEF project database search</li>
            </ul>
          </div>

          {/* Get Access Code Section */}
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-800 mb-2">
              Get Access Code
            </h3>
            <div className="text-xs text-blue-700 space-y-2">
              <p>• <strong>Evaluation:</strong> <a href="https://scienceresearchai.com/#evaluation" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-800">Request on website</a></p>
              <p>• <strong>Teacher Pro:</strong> <a href="https://scienceresearchai.com/#pricing" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-800">30-day license</a></p>
              <p>• <strong>School License:</strong> <a href="https://scienceresearchai.com/#contact" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-800">Institution access</a></p>
            </div>
            <a
              href="https://scienceresearchai.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-3 text-xs text-blue-600 hover:text-blue-800 underline"
            >
              Visit Main scienceresearchai.com →
            </a>
          </div>

          {/* Connection Status */}
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 text-sm">
              {connectionStatus === 'connected' ? (
                <>
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-green-600">Server Connected</span>
                </>
              ) : connectionStatus === 'unstable' ? (
                <>
                  <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                  <span className="text-yellow-600">Connection Unstable</span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-red-600">Connection Failed</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main app render - Fully responsive design
  return (
    <div className="flex h-screen bg-gray-50 relative">
      {/* Mobile Warning Banner */}
      {isMobileDevice && showMobileWarning && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white px-4 py-3 shadow-lg">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-2 flex-1">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-xs sm:text-sm font-medium">
                📱 This service is optimized for <strong>tablet and desktop/laptop</strong>. Please use a larger screen for the best experience.
              </p>
            </div>
            <button
              onClick={() => setShowMobileWarning(false)}
              className="ml-3 flex-shrink-0 text-white hover:text-amber-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Enhanced Sidebar with responsive design - Optimized for tablet/desktop */}
      <div className={`
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        fixed lg:static inset-y-0 left-0 z-50
        w-72 sm:w-64 lg:w-72
        bg-white border-r border-gray-200
        flex flex-col shadow-lg
        transform transition-transform duration-300 ease-in-out
      `}>
        {/* Header - Compact design */}
        <div className="p-2 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
          {/* Mobile header with close button */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-1.5">
              <Beaker className="w-4 h-4 text-blue-600" />
              <h1 className="text-sm font-semibold text-gray-900">Science Research AI</h1>
            </div>

            <div className="flex items-center space-x-1.5">
              {/* Connection status */}
              {connectionStatus === 'connected' ? (
                <Wifi className="w-3.5 h-3.5 text-green-500" />
              ) : (
                <WifiOff className="w-3.5 h-3.5 text-red-500" />
              )}

              {/* Close button (mobile only) */}
              <button
                onClick={closeSidebar}
                className="lg:hidden p-1 hover:bg-gray-200 rounded"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Compact License info */}
          {licenseInfo && (
            <div className="p-2 border border-gray-200 rounded bg-white">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center space-x-1.5">
                  <User className="w-3 h-3 text-blue-600" />
                  <span className="font-medium text-blue-800">
                    {licenseInfo?.displayName ||
                     (userStatus === 'trial' ? 'Free Trial' :
                      userStatus === 'premium' ? 'Premium' :
                      userStatus === 'unlimited' ? 'Unlimited' : 'Guest')}
                  </span>
                </div>
                {licenseInfo?.statusIcon && (
                  <span className="text-xs">{licenseInfo.statusIcon}</span>
                )}
              </div>
              {timeLeft !== Infinity && (
                <div className="flex items-center space-x-1 text-xs text-gray-600 mt-1">
                  <Clock className="w-3 h-3 text-gray-500" />
                  <span>{formatTimeLeft(timeLeft)}</span>
                </div>
              )}
              {/* API Usage Display - Chat counter hidden */}
              {isAuthenticated && (
                <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">📄 Docs</span>
                    <span className={`font-medium ${apiUsage.docCount >= apiUsage.docLimit ? 'text-red-500' : 'text-gray-700'}`}>
                      {apiUsage.docCount}/{apiUsage.docLimit}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Enhanced Function buttons - Compact design for better visibility */}
        <div className="p-2 sm:p-3 space-y-2 flex-1 overflow-y-auto">
          {/* Research Plan Button */}
          <button
            onClick={() => {
              triggerResearchPlan();
              closeSidebar(); // Close sidebar on mobile after action
            }}
            disabled={!planningButtonEnabled || isLoading}
            className={`w-full p-2 sm:p-2.5 rounded-lg transition-all duration-200 flex items-center space-x-2 text-left ${
              planningButtonEnabled
                ? 'bg-green-600 text-white hover:bg-green-700 shadow-md hover:shadow-lg active:scale-95'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
          >
            <FileText className="w-4 h-4 flex-shrink-0" />
            <div className="min-w-0">
              <div className="font-medium text-sm">🔬 Research Plan</div>
              <div className="text-xs opacity-80">Generate experiment design</div>
            </div>
          </button>

          {/* Experiment Report Button - requires research plan first */}
          <button
            onClick={() => {
              triggerExperimentReport();
              closeSidebar();
            }}
            disabled={!reportButtonEnabled || isLoading || !(documentMode === 'research-plan' && documentContent)}
            className={`w-full p-2 sm:p-2.5 rounded-lg transition-all duration-200 flex items-center space-x-2 text-left ${
              reportButtonEnabled && documentMode === 'research-plan' && documentContent
                ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-md hover:shadow-lg active:scale-95'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
          >
            <BarChart3 className="w-4 h-4 flex-shrink-0" />
            <div className="min-w-0">
              <div className="font-medium text-sm">📝 Lab Report</div>
              <div className="text-xs opacity-80">
                {documentMode === 'research-plan' && documentContent
                  ? 'Create formatted report'
                  : 'Create research plan first'}
              </div>
            </div>
          </button>

          {/* Competition Projects Search Button */}
          <button
            onClick={() => {
              triggerCompetitionSearch();
              closeSidebar();
            }}
            disabled={isLoading}
            className="w-full p-2 sm:p-2.5 rounded-lg transition-all duration-200 flex items-center space-x-2 text-left bg-indigo-600 text-white hover:bg-indigo-700 shadow-md hover:shadow-lg active:scale-95"
          >
            <FlaskConical className="w-4 h-4 flex-shrink-0" />
            <div className="min-w-0">
              <div className="font-medium text-sm">🏆 Competition Projects</div>
              <div className="text-xs opacity-80">ISEF & AI search</div>
            </div>
          </button>

          {/* Start Over Button */}
          <button
            onClick={() => {
              resetAllData();
              closeSidebar();
            }}
            className="w-full p-2 sm:p-2.5 rounded-lg transition-all duration-200 flex items-center space-x-2 text-left bg-gray-600 text-white hover:bg-gray-700 shadow-md active:scale-95"
          >
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">🔄</div>
            <div className="min-w-0">
              <div className="font-medium text-sm">Start Over</div>
              <div className="text-xs opacity-80">Reset chat</div>
            </div>
          </button>
        </div>
        
        {/* Current topic display - Compact */}
        {currentResearchTopic && (
          <div className="p-2 border-t border-gray-100">
            <div className="text-xs text-gray-600 mb-1">Current Topic</div>
            <div className="text-xs font-medium text-gray-900 bg-blue-50 px-2 py-1.5 rounded break-words">
              {currentResearchTopic}
            </div>
          </div>
        )}

        {/* Chat Memory - Compact */}
        <div className="p-2 border-t border-gray-100">
          <div className="text-xs text-gray-600 mb-1.5">Chat Memory</div>
          <div className="flex space-x-1">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded ${
                  i < Math.ceil(calculateUsage() / 20) ? getUsageColor().replace('bg-', 'bg-') : 'bg-gray-200'
                }`}
              ></div>
            ))}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {calculateUsage() < 20 ? 'Start conversation' :
             calculateUsage() < 60 ? 'Topic forming' :
             calculateUsage() < 80 ? 'Deep discussion' :
             'Near limit'}
          </div>
        </div>

        {/* Logout button - Compact */}
        <div className="p-2 border-t border-gray-100">
          <button
            onClick={handleLogout}
            className="w-full bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-1.5 active:scale-95 text-sm"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Logout</span>
          </button>
        </div>
      </div>
      
      {/* Chat area - responsive */}
      <div className={`flex-1 flex flex-col ${
        isDocumentFullscreen ? 'hidden' : ''
      } ${
        (documentContent || isDocumentGenerating) ? 'lg:w-1/2' : ''
      }`}>
        {/* Mobile header with hamburger menu */}
        <div className="lg:hidden flex items-center justify-between p-4 bg-white border-b border-gray-200">
          <button
            onClick={toggleSidebar}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Menu className="w-6 h-6 text-gray-600" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Science Research AI</h1>
          <div className="w-10"></div> {/* Spacer for centering */}
        </div>

        {/* Token usage display - moved from sidebar */}
        <div className="bg-white border-b border-gray-200 px-4 py-2">
          <div className="flex items-center justify-between text-xs text-gray-600">
            <span className="leading-tight">Please reset when conversations get long</span>
            <span className="font-mono">
              {getCurrentTokens().toLocaleString()}/{maxTokens.toLocaleString()} ({Math.round(calculateUsage())}%)
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
            <div 
              className={`h-1.5 rounded-full transition-all duration-300 ${getUsageColor()}`}
              style={{ width: `${calculateUsage()}%` }}
            ></div>
          </div>
        </div>
        
        {/* Messages - responsive */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">
          
          {/* Example buttons - only show when no user messages */}
          {messages.filter(msg => msg.role === 'user').length === 0 && (
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-3">Click on a topic that interests you:</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setInputValue('I want to do microplastic degradation experiments')}
                  className="px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm border border-blue-200"
                >
                  🔬 Microplastic Degradation
                </button>
                <button
                  onClick={() => setInputValue('I want to design plant growth experiments')}
                  className="px-3 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm border border-green-200"
                >
                  🌱 Plant Growth Research
                </button>
                <button
                  onClick={() => setInputValue('I want to experiment with diode logic circuits')}
                  className="px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-sm border border-purple-200"
                >
                  ⚡ Electronic Circuits
                </button>
                <button
                  onClick={() => setInputValue('I want to research solar cell efficiency improvement')}
                  className="px-3 py-2 bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 transition-colors text-sm border border-yellow-200"
                >
                  ☀️ Solar Energy Research
                </button>
                <button
                  onClick={() => setInputValue('I want to try nanotube synthesis experiments')}
                  className="px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors text-sm border border-indigo-200"
                >
                  🧪 Nanomaterial Synthesis
                </button>
                <button
                  onClick={() => setInputValue('I want to do environmental science experiments')}
                  className="px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors text-sm border border-emerald-200"
                >
                  🌍 Environmental Science
                </button>
              </div>
            </div>
          )}
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] sm:max-w-3xl px-3 sm:px-4 py-2 sm:py-3 rounded-lg ${
                  message.role === 'user'
                    ? 'bg-blue-600 text-white ml-4 sm:ml-8'
                    : 'bg-white border border-gray-200 mr-4 sm:mr-8 shadow-sm'
                }`}
              >
                <div className="prose prose-sm max-w-none text-sm sm:text-base">
                  <div className="whitespace-pre-wrap break-words">
                    {message.role === 'assistant' && typingMessageIndex === index && !message.isComplete
                      ? displayedText + '▋'
                      : message.content
                    }
                  </div>
                </div>
                <div className="text-xs opacity-70 mt-1 sm:mt-2">
                  {message.timestamp}
                </div>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-200 rounded-lg px-3 sm:px-4 py-2 sm:py-3 mr-4 sm:mr-8 shadow-sm">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-gray-600 text-sm sm:text-base">Thinking...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input area - responsive */}
        <div className="border-t border-gray-200 p-3 sm:p-4 bg-white">
          <div className="flex space-x-2 sm:space-x-3">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask anything about science experiments..."
              className="flex-1 px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm sm:text-base"
              disabled={isLoading}
            />
            
            {/* Stop button - show only when AI is responding */}
            {(canStop || typingMessageIndex !== null) && (
              <button
                onClick={handleStopGeneration}
                className="bg-red-600 text-white px-3 sm:px-4 py-3 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-1 sm:space-x-2 active:scale-95"
                title="Stop generation"
              >
                <div className="w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-sm"></div>
                <span className="text-xs sm:text-sm font-medium">Stop</span>
              </button>
            )}
            
            {/* Send button */}
            <button
              onClick={handleSendMessage}
              disabled={isLoading || !inputValue.trim()}
              className="bg-blue-600 text-white px-4 sm:px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 active:scale-95"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
       </div>
      
      {/* Document panel - responsive */}
      {(documentContent || isDocumentGenerating) && (
        <div className={`
          ${isDocumentFullscreen 
            ? 'fixed inset-0 z-30' 
            : 'hidden lg:flex lg:w-1/2'
          }
          bg-white border-l border-gray-200 flex-col
        `}>
          {/* Document header - responsive */}
          <div className="p-3 sm:p-4 border-b border-gray-200 flex items-center justify-between bg-white">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">
              {documentMode === 'research-plan' ? 'Research Plan' : 'Experiment Report'}
            </h2>
            <div className="flex items-center space-x-2">
              {/* Mobile fullscreen toggle */}
              <button
                onClick={toggleDocumentFullscreen}
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {isDocumentFullscreen ? (
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                ) : (
                  <X className="w-5 h-5 text-gray-600" />
                )}
              </button>
              
              {documentContent && !isDocumentGenerating && (
                <button
                  onClick={handlePDFDownload}
                  className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1 text-sm active:scale-95"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">PDF</span>
                </button>
              )}
              <button
                onClick={handleCloseDocument}
                className="text-gray-500 hover:text-gray-700 transition-colors p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Document content - responsive */}
          <div className="flex-1 overflow-y-auto">
            {isDocumentGenerating ? (
              <div className="p-4 sm:p-8 space-y-4 sm:space-y-6">
                <div className="text-center">
                  <div className="bg-blue-100 rounded-full p-3 sm:p-4 w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4">
                    <FlaskConical className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 animate-pulse" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                    Generating Document...
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 sm:mb-6">
                    {documentProgressMessage}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Progress</span>
                    <span>{documentProgress}%</span>
                  </div>
                  <div className="bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${documentProgress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ) : (
              <div 
                className="p-3 sm:p-6 text-sm sm:text-base"
                dangerouslySetInnerHTML={{ __html: documentContent }}
              />
            )}
          </div>
        </div>
      )}

      {/* Floating action button for document (mobile only) */}
      {(documentContent || isDocumentGenerating) && !isDocumentFullscreen && (
        <button
          onClick={toggleDocumentFullscreen}
          className="lg:hidden fixed bottom-20 right-4 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-20 active:scale-95"
        >
          <FileText className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
